/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpResponse} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {EsciComponent} from '../components/esci.component';
import {KtsComponent} from '../components/kts/kts.component';
import {MomentumProgram} from '../model/momentumProgram.model';
import {KtsTimingTemplateRow} from '../model/ktsTimingTemplateRow.model';
import {finalize, forkJoin, map, Observable, of, ReplaySubject, shareReplay, Subject, tap, timeout} from 'rxjs';
import {ProgramRun} from '../model/programRun';
import {GpsRpo} from '../model/gps-rpo';
import type {GpsRpoManagementComponent} from '../components/gps-rpo-management/gps-rpo-management.component';
import {GpsTimingComponent} from '../components/gps-timing/gps-timing.component';
import {EditableGridComponent} from '../components/editable-grid-component';
import {GpsTiming} from '../model/gpsProgramTiming.model';
import {RestrictedProgramsComponent} from '../components/restricted-programs/restricted-programs.component';
import {ModelYear} from '../model/modelYear.model';
import {RestrictedMomentumProgram} from '../model/restrictedMomentumProgram.model';
import {MomentumProgramAccess} from '../model/momentumProgramAccess';
import {IcdProgramTimingComponent} from '../components/icd-program-timing/icd-program-timing.component';
import {ValidationModel} from '../model/validation.model';
import {ObjectDescription} from '../model/objectDescription';
import {getComponentImageIdsForType, normalizeComponentImageType} from '../utils/component-image-types.util';
import {WorkloadTimingComponent} from '../components/workload-timing/workload-timing.component';
import {ProgramSize} from '../model/programSize';
import {WorkloadTimingSize} from '../model/workloadTimingSize';
import {KtsEditorComponent} from '../components/kts-editor/kts-editor.component';
import {UrlRequestActivityReport} from '../model/urlRequestActivityReport';
import {UrlRequestLog} from '../model/urlRequestLog';
import {PagingResult} from '../model/pagingResult';
import {buildGridPageParams, ComponentIdOption, GridPageRequest} from '../model/gridPageRequest';
import {ReleaseSchedule} from "../model/releaseSchedule.model";
import {
    IcdSubscriptionListItem,
    IcdSubscriptionModelYearOption,
    IcdSubscriptionProgramOption
} from '../model/icdSubscription.model';
import {
    CreateIcdsFromTemplatesResponse,
    IcdRefMapStatus,
    IcdTemplateInventory,
    IoMatrixLoadResponse,
    IoMatrixRow,
    IoMatrixSnapshot
} from '../model/io-matrix.models';
import {
    AggregatorSignalKey,
    AggregatorSignalRow,
    EioEnumOption,
    IoTypeDesignatorOption,
    PlatingOption,
    TerminalSizeOption
} from '../model/aggregator-signal.models';
import {
    AggregatorAutoMatchRules,
    AggregatorCadenceUpgrade,
    AggregatorIoMatrixCadence,
    AggregatorIoMatrixInventory,
    AggregatorIoMatrixLoadResponse,
    AggregatorIoMatrixRow,
    AggregatorOption
} from '../model/aggregator-io-matrix.models';
import {RoleModificationOptions} from '../model/roleModificationOptions.model';

export type IcdSelectOption = { label: string; value: string };

/** Allowed program for Create Aggregator ICDs, including Type used by the ICE/EV mismatch warning. */
export interface AggregatorAllowedProgram {
    programName: string;
    programTypeList: string[];
    programTypeDisplay: string;
    resolvedPowerType?: 'ICE' | 'EV' | null;
}

export interface CreateIcdFormOptionsResponse {
    buildStatusOptions: string[];
    revisionOptions: string[];
    deviceVariantOptions: string[];
    componentSourcedOptions: IcdSelectOption[];
    operateNormallyOptions: IcdSelectOption[];
    fullyFunctionalOptions: IcdSelectOption[];
    securityClassifierOptions: IcdSelectOption[];
}

@Injectable({
    providedIn: 'root'
})
export class EsciService {
    private readonly permissionRequestGenerations = new WeakMap<EsciComponent, number>();

    constructor(private http: HttpClient) {
    }

    // --- Change Roles (IT Admin Tools) ---
    public getRoleModificationOptionsUrl = '/esciUI/roleModificationOptions';
    public setTemporaryRolesUrl = '/esciUI/roleModification/setRoles';
    public clearTemporaryRolesUrl = '/esciUI/roleModification/clearRoles';

    public getRoleModificationOptions(): Observable<RoleModificationOptions> {
        return this.http.get<RoleModificationOptions>(
            `${environment.apiUrl}${this.getRoleModificationOptionsUrl}`);
    }

    public setTemporaryRoles(roles: string[], timeoutMinutes: number): Observable<string> {
        let params = new HttpParams().set('timeoutMinutes', timeoutMinutes);
        for (const role of roles) {
            params = params.append('roles', role);
        }
        return this.http.post(`${environment.apiUrl}${this.setTemporaryRolesUrl}`, null,
            {params, responseType: 'text'});
    }

    public clearTemporaryRoles(): Observable<string> {
        return this.http.post(`${environment.apiUrl}${this.clearTemporaryRolesUrl}`, null,
            {responseType: 'text'});
    }

    public buildCheckToolFileSizeUrl = '/esciUI/buildCheckToolFileSizeUrl';
    public extractionCompareFileSizeUrl = '/esciUI/extractionCompareFileSizeUrl';
    public buildCheckToolProgramListUrl = '/esciUI/buildCheckToolProgramList';
    public buildCheckToolModelYearListUrl = '/esciUI/buildCheckToolModelYearList';
    public ktsMomentumProgramListUrl = '/esciUI/ktsMomentumProgramList';
    public ktsEcsMomentumProgramsUrl = '/esciUI/ktsEcsMomentumPrograms';
    public ktsMomentumProgramByMomentumIdUrl = '/esciUI/ktsMomentumProgramByMomentumId';
    public getKtsProgramRunsUrl = '/esciUI/getKtsProgramRuns';
    public getKtsProdProgramRunsForIcdTimingUrl = '/esciUI/getKtsProdProgramRunsForIcdTiming';
    public getMomentumProgramsForIcdTimingUrl = '/esciUI/getMomentumProgramsForIcdTiming';
    public getIcdProgramsForIcdTimingUrl = '/esciUI/getIcdProgramsForIcdTiming';
    public getAllowedIoMatrixProgramsUrl = '/esciUI/icd/ioMatrix/allowedPrograms';
    public getDescribeObjectUrl = '/esciUI/describeObject';
    public getDescribeObjectsUrl = '/esciUI/describeObjects';
    public getComponentImageTypesUrl = '/esciUI/componentImageTypes';
    public getObjectsUrl = '/esciUI/getObjects';
    public getObjectsForExportUrl = '/esciUI/getObjectsForExport';
    public getObjectColumnValuesUrl = '/esciUI/getObjectColumnValues';
    public getObjectIdOptionsUrl = '/esciUI/getObjectIdOptions';
    public exportObjectsUrl = '/esciUI/exportObjects';
    public getComponentIdPickerRowsUrl = '/esciUI/componentIdPicker';
    public getHistoryUrl = '/esciUI/getHistory';
    public getGpsMomentumDataUrl = '/esciUI/getGpsMomentumData';
    public getGpsTimingUrl = '/esciUI/getGpsTiming';
    public getRestrictedMomentumProgramsUrl = '/esciUI/getRestrictedMomentumPrograms';
    public getMomentumProgramAccessUrl = '/esciUI/getMomentumProgramAccess';
    public deleteKtsTemplateUrl = '/esciUI/deleteKtsTemplate';
    private buttonClickSubject = new Subject<any>();
    buttonClick$ = this.buttonClickSubject.asObservable();
    private objectDescriptions: ObjectDescription[] | null = null;
    private componentImageTypes: string[] | null = null;
    private componentImageTypesRequest$: Observable<string[]> | null = null;
    private objDescriptionMapByName = new Map<string, ObjectDescription>();
    private componentIdPickerRowsBySource = new Map<string, ReplaySubject<any[]>>();
    private componentIdPickerRefreshesInFlight = new Set<string>();
    private static readonly componentIdPickerSourceByComponentName: Record<string, string> = {
        ConnectorCatalog: 'connectorCatalogIds',
        Terminal: 'terminalComponentIds',
        Seal: 'sealComponentIds',
        Plug: 'plugComponentIds',
        SubComponent: 'subComponentIds'
    };
    public savePrimaryTimingProgramsUrl = '/esciUI/savePrimaryTimingPrograms'
    public getProgramSizeUrl = '/esciUI/getProgramSize';
    public getWorkloadTimingSizeUrl = '/esciUI/getWorkloadTimingSize';
    public getKtsTimingTemplatePrefixesUrl = '/esciUI/getKtsTimingTemplatePrefixes';
    public getKtsTimingTemplateRowsForEditorUrl = '/esciUI/ktsTimingTemplateRowsForEditor';
    public deleteProgramRunsUrl = '/esciUI/deleteProgramRuns';
    public getUrlRequestActivityReportUrl = '/esciUI/urlRequestActivityReport';
    public getUrlRequestLogsUrl = '/esciUI/urlRequestLogs';
    public capitalComponentUsageSubmitUrl = '/esciUI/capitalComponentUsageSubmit';
    public capitalComponentUsageStatusUrl = '/esciUI/capitalComponentUsageStatus/';
    public objectCodeListUrl              = '/esciUI/objectCodeList';
    public getReleaseBannerMessageUrl = '/esciUI/releaseBanner';
    public getScheduledReleaseBannerMessageUrl = '/esciUI/scheduledReleaseBanner';
    public getDefaultGridFiltersUrl = '/esciUI/defaultGridFilters';
    public getReleaseSchedulesUrl = '/esciUI/releaseSchedules';
    public getIcdSubscriptionsUrl = '/esciUI/icdSubscription/list';
    public addIcdSubscriptionUrl = '/esciUI/icdSubscription/icd';
    public addIcdSubscriptionsUrl = '/esciUI/icdSubscription/icds';
    public addIcdSubscriptionGroupUrl = '/esciUI/icdSubscription/group';
    public getIcdSubscriptionGroupPreviewCountUrl = '/esciUI/icdSubscription/group/previewCount';
    public getIcdSubscriptionStatusUrl = '/esciUI/icdSubscription/status';
    public getIcdSubscriptionProgramOptionsUrl = '/esciUI/icdSubscription/programOptions';
    public getIcdSubscriptionModelYearOptionsUrl = '/esciUI/icdSubscription/modelYearOptions';
    public getIoMatrixSourceRowsUrl = '/esciUI/ioMatrix/sourceRows';
    public getIoMatrixSnapshotsUrl = '/esciUI/ioMatrix/list';
    public getIoMatrixRowsUrl = '/esciUI/ioMatrix/rows';
    public saveIoMatrixUrl = '/esciUI/ioMatrix/save';
    public aggregatorSignalPermissionUrl = '/esciUI/aggregatorSignal/permission';
    public aggregatorSignalAdminPermissionUrl = '/esciUI/aggregatorSignal/adminPermission';
    public getAggregatorSignalObjectCodesUrl = '/esciUI/aggregatorSignal/objectCodes';
    public getAggregatorSignalKeysUrl = '/esciUI/aggregatorSignal/keys';
    public getAggregatorSignalRowsUrl = '/esciUI/aggregatorSignal/rows';
    public saveAggregatorSignalUrl = '/esciUI/aggregatorSignal/save';
    public getAggregatorSignalHistoryUrl = '/esciUI/aggregatorSignal/history';
    public getAggregatorSignalIcdCompareUrl = '/esciUI/aggregatorSignal/icdCompare';
    public getAggregatorSignalIoTypesUrl = '/esciUI/aggregatorSignal/ioTypes';
    public getAggregatorSignalTerminalSizesUrl = '/esciUI/aggregatorSignal/terminalSizes';
    public getAggregatorSignalPlatingsUrl = '/esciUI/aggregatorSignal/platings';
    public getAggregatorSignalPwmGroupsUrl = '/esciUI/aggregatorSignal/pwmGroups';
    public getAggregatorSignalVRefMappingsUrl = '/esciUI/aggregatorSignal/vRefMappings';
    public getAggregatorSignalFlexibleDiscreteOutputsUrl = '/esciUI/aggregatorSignal/flexibleDiscreteOutputs';
    public getAggregatorSignalCreateObjectCodesUrl = '/esciUI/aggregatorSignal/createObjectCodes';
    public deleteAggregatorSignalTableUrl = '/esciUI/aggregatorSignal/deleteTable';
    public getAggregatorSignalHistoryAllUrl = '/esciUI/aggregatorSignal/historyAll';
    public deleteAggregatorIoMatrixUrl = '/esciUI/aggregatorIoMatrix/deleteMatrix';
    public getAggregatorIoMatrixHistoryAllUrl = '/esciUI/aggregatorIoMatrix/historyAll';
    public aggregatorIoMatrixPermissionUrl = '/esciUI/aggregatorIoMatrix/permission';
    public getAggregatorIoMatrixObjectCodesUrl = '/esciUI/aggregatorIoMatrix/objectCodes';
    public getAggregatorIoMatrixArchitecturesUrl = '/esciUI/aggregatorIoMatrix/architectures';
    public getAggregatorIoMatrixPowerTypesUrl = '/esciUI/aggregatorIoMatrix/powerTypes';
    public getAggregatorIoMatrixKeywordsUrl = '/esciUI/aggregatorIoMatrix/keywords';
    public getAggregatorIoMatrixCadenceUrl = '/esciUI/aggregatorIoMatrix/cadence';
    public getAggregatorIoMatrixAggregatorOptionsUrl = '/esciUI/aggregatorIoMatrix/aggregatorOptions';
    public getAggregatorIoMatrixRowsUrl = '/esciUI/aggregatorIoMatrix/rows';
    public saveAggregatorIoMatrixUrl = '/esciUI/aggregatorIoMatrix/save';
    public autoMatchAggregatorIoMatrixUrl = '/esciUI/aggregatorIoMatrix/autoMatch';
    public autoMatchRulesAggregatorIoMatrixUrl = '/esciUI/aggregatorIoMatrix/autoMatchRules';
    public cadenceUpgradeAggregatorIoMatrixUrl = '/esciUI/aggregatorIoMatrix/cadenceUpgrade';
    public getAggregatorIoMatrixReportUrl = '/esciUI/aggregatorIoMatrix/report';
    public getAggregatorIoMatrixHistoryUrl = '/esciUI/aggregatorIoMatrix/history';
    public getAggregatorIoMatrixIcdCompareUrl = '/esciUI/aggregatorIoMatrix/icdCompare';
    public getAggregatorIoMatrixRefMapStatusUrl = '/esciUI/aggregatorIoMatrix/refMapStatus';
    public aggregatorIoMatrixIcdPermissionUrl = '/esciUI/aggregatorIoMatrixIcd/permission';
    public getAggregatorIoMatrixIcdAllowedProgramsUrl = '/esciUI/aggregatorIoMatrixIcd/allowedPrograms';
    public getAggregatorIoMatrixIcdRefMapStatusUrl = '/esciUI/aggregatorIoMatrixIcd/refMapStatus';
    public getAggregatorIoMatrixIcdMatrixInventoryUrl = '/esciUI/aggregatorIoMatrixIcd/matrixInventory';
    public getAggregatorIoMatrixIcdObjectCodesUrl = '/esciUI/aggregatorIoMatrixIcd/objectCodes';
    public getAggregatorIoMatrixIcdArchitecturesUrl = '/esciUI/aggregatorIoMatrixIcd/architectures';
    public getAggregatorIoMatrixIcdPowerTypesUrl = '/esciUI/aggregatorIoMatrixIcd/powerTypes';
    public getAggregatorIoMatrixIcdKeywordsUrl = '/esciUI/aggregatorIoMatrixIcd/keywords';
    public getIcdTemplateCircuitOptionsUrl = '/esciUI/icd/circuitOptions';
    public getIcdCircuitSearchUrl = '/esciUI/icd/circuitSearch';
    public saveIcdTemplateValuesUrl = '/esciUI/icd/ioMatrix/icdValues/save';
    public startFreshIcdTemplateUrl = '/esciUI/icd/ioMatrix/startFresh';
    public getIcdTemplateInventoryUrl = '/esciUI/icd/templateInventory';
    public getIcdTemplateDetailUrl = '/esciUI/icd/templateInventory';
    public createIcdsFromTemplateUrl = '/esciUI/icd/templateInventory/createIcds';
    public createAggregatorIcdUrl = '/esciUI/aggregatorIoMatrixIcd/createIcd';
    public getCreateIcdFormOptionsUrl = '/esciUI/icd/templateInventory/createIcdFormOptions';
    public getAggregatorIoMatrixIcdCreateIcdFormOptionsUrl =
        '/esciUI/aggregatorIoMatrixIcd/createIcdFormOptions';
    public cadenceUpgradeUrl = '/esciUI/ioMatrix/cadenceUpgrade';
    public getIcdObjectCodesUrl = '/esciUI/icd/objectCodes';
    public getIcdObjectCodeInstsUrl = '/esciUI/icd/objectCodeInsts';
    public getIcdControllerDesignatorsUrl = '/esciUI/icd/controllerDesignators';
    public getIcdArchitecturesUrl = '/esciUI/icd/architectures';
    public getIcdRefMapStatusUrl = '/esciUI/icd/refMapStatus';
    public getIoMatrixHistoryUrl = '/esciUI/ioMatrix/history';
    public getIcdTemplateHistoryUrl = '/esciUI/icd/ioMatrix/templateHistory';
    public getIoMatrixIcdTemplateHistoryUrl = '/esciUI/icd/ioMatrixIcdTemplate/history';
    public getIoMatrixIcdTemplateReportUrl = '/esciUI/icd/ioMatrixIcdTemplate/report';

    private static handleError(err: any, component: EsciComponent) {
        console.log(err);
        let error = 'Failed to save data. Please try again later.';
        if (err.error && typeof err.error ===  'string') {
            error = err.error;
        } else if (err.error && err.error.message) {
            error = err.error.message;
        } else if (err.message) {
            error = err.message;
        }
        // Check for duplicate key pattern
        const match = error.match(/=\(([^)]+)\) already exists/);
        if (match) {
            component.messageService.processError(`A record with the value "${match[1]}" already exists. Please use a unique value and try again.`);
        } else {
            // if err has error message trim to 600 char so it is viewable in the toast
            component.messageService.processError(error.length > 600 ?
                error.substring(0, 600) + '...' : error);
        }
    }

    private static handleSaveResponse(event: any, component: EsciComponent, dataType: string) {
        if (event instanceof HttpResponse) {
            if (event.status === 200) {
                component.messageService.processSuccess(`${dataType} data saved successfully.`);
            } else {
                component.messageService.processError(`${dataType} data not saved successfully.`);
            }
        }
    }

    emitTimingAdjustment(params: any): void {
        this.buttonClickSubject.next(params);
    }

    private makeServiceCall(url: string) {
        const req = new HttpRequest('GET', `${environment.apiUrl}${url}`);
        return this.http.request(req);
    }

    private makeServiceCallWithParams(url: string, params: HttpParams) {
        const req = new HttpRequest('GET', environment.apiUrl + url, {params: params});
        return this.http.request(req);
    }

    private setComponentStateDeferred(component: any, key: 'loading' | 'saving', value: boolean): void {
        component[key] = value;
    }

    private syncRowDataSignal(component: any): void {
        if (component && typeof component.syncRowDataSignal === 'function') {
            component.syncRowDataSignal();
        }
    }

    private isGridApiAlive(gridApi: any): boolean {
        if (!gridApi) {
            return false;
        }
        return typeof gridApi.isDestroyed !== 'function' || !gridApi.isDestroyed();
    }

    public getReleaseBannerMessage(): Observable<string> {
        return this.http.get(`${environment.apiUrl}${this.getReleaseBannerMessageUrl}`, {responseType: 'text'});
    }

    public getScheduledReleaseBannerMessage(): Observable<string> {
        return this.http.get(`${environment.apiUrl}${this.getScheduledReleaseBannerMessageUrl}`, {responseType: 'text'});
    }

    public getDefaultGridFilters(): Observable<Record<string, string[]>> {
        return this.http.get<Record<string, string[]>>(`${environment.apiUrl}${this.getDefaultGridFiltersUrl}`);
    }

    public getReleaseSchedules(): Observable<ReleaseSchedule[]> {
        return this.http.get<ReleaseSchedule[]>(`${environment.apiUrl}${this.getReleaseSchedulesUrl}`);
    }

    public getIoMatrixSourceRows(objectCode: string, objectCodeInst: string, architecture: string): Observable<IoMatrixRow[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('objectCodeInst', objectCodeInst)
            .set('architecture', architecture);
        return this.http.get<IoMatrixRow[]>(`${environment.apiUrl}${this.getIoMatrixSourceRowsUrl}`, {params});
    }

    public getIoMatrixSnapshots(): Observable<IoMatrixSnapshot[]> {
        return this.http.get<IoMatrixSnapshot[]>(`${environment.apiUrl}${this.getIoMatrixSnapshotsUrl}`);
    }

    public getIoMatrixRows(matrixId: string, cadenceVer?: string): Observable<IoMatrixLoadResponse> {
        let params = new HttpParams().set('matrixId', matrixId);
        if (cadenceVer) {
            params = params.set('cadenceVer', cadenceVer);
        }
        return this.http.get<IoMatrixLoadResponse>(`${environment.apiUrl}${this.getIoMatrixRowsUrl}`, {params});
    }

    public getAggregatorSignalObjectCodes(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}${this.getAggregatorSignalObjectCodesUrl}`);
    }

    public getAggregatorSignalKeys(): Observable<AggregatorSignalKey[]> {
        return this.http.get<AggregatorSignalKey[]>(`${environment.apiUrl}${this.getAggregatorSignalKeysUrl}`);
    }

    public getAggregatorSignalRows(objectCode: string, architecture: string, powerType: string,
                                   keyword: string = ''): Observable<AggregatorSignalRow[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.get<AggregatorSignalRow[]>(`${environment.apiUrl}${this.getAggregatorSignalRowsUrl}`, {params});
    }

    public saveAggregatorSignal(objectCode: string, architecture: string, powerType: string,
                                keyword: string, rows: AggregatorSignalRow[]): Observable<any> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.post(`${environment.apiUrl}${this.saveAggregatorSignalUrl}`, rows || [], {
            params,
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    public deleteAggregatorSignalTable(objectCode: string, architecture: string, powerType: string,
                                       keyword: string = ''): Observable<any> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.post(`${environment.apiUrl}${this.deleteAggregatorSignalTableUrl}`, null, {params});
    }

    public getAggregatorSignalHistory(objectCode: string, architecture: string, powerType: string,
                                      keyword: string = ''): Observable<ArrayBuffer> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.get(`${environment.apiUrl}${this.getAggregatorSignalHistoryUrl}`,
                             {params, responseType: 'arraybuffer'});
    }

    public getAggregatorSignalHistoryAll(): Observable<ArrayBuffer> {
        return this.http.get(`${environment.apiUrl}${this.getAggregatorSignalHistoryAllUrl}`,
                             {responseType: 'arraybuffer'});
    }

    public getAggregatorSignalIcdCompare(objectCode: string, architecture: string, powerType: string,
                                         keyword: string = ''): Observable<ArrayBuffer> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.get(`${environment.apiUrl}${this.getAggregatorSignalIcdCompareUrl}`,
                             {params, responseType: 'arraybuffer'});
    }

    public getAggregatorSignalIoTypes(): Observable<IoTypeDesignatorOption[]> {
        return this.http.get<IoTypeDesignatorOption[]>(`${environment.apiUrl}${this.getAggregatorSignalIoTypesUrl}`);
    }

    public getAggregatorSignalTerminalSizes(): Observable<TerminalSizeOption[]> {
        return this.http.get<TerminalSizeOption[]>(`${environment.apiUrl}${this.getAggregatorSignalTerminalSizesUrl}`);
    }

    public getAggregatorSignalPlatings(): Observable<PlatingOption[]> {
        return this.http.get<PlatingOption[]>(`${environment.apiUrl}${this.getAggregatorSignalPlatingsUrl}`);
    }

    public getAggregatorSignalPwmGroups(): Observable<EioEnumOption[]> {
        return this.http.get<EioEnumOption[]>(`${environment.apiUrl}${this.getAggregatorSignalPwmGroupsUrl}`);
    }

    public getAggregatorSignalVRefMappings(): Observable<EioEnumOption[]> {
        return this.http.get<EioEnumOption[]>(`${environment.apiUrl}${this.getAggregatorSignalVRefMappingsUrl}`);
    }

    public getAggregatorSignalFlexibleDiscreteOutputs(): Observable<EioEnumOption[]> {
        return this.http.get<EioEnumOption[]>(
                `${environment.apiUrl}${this.getAggregatorSignalFlexibleDiscreteOutputsUrl}`);
    }

    public getAggregatorSignalCreateObjectCodes(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}${this.getAggregatorSignalCreateObjectCodesUrl}`);
    }

    public checkAggregatorIoMatrixPermission(): Observable<boolean> {
        return this.http.get<boolean>(`${environment.apiUrl}${this.aggregatorIoMatrixPermissionUrl}`);
    }

    public getAggregatorIoMatrixObjectCodes(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}${this.getAggregatorIoMatrixObjectCodesUrl}`);
    }

    public getAggregatorIoMatrixArchitectures(objectCode: string): Observable<string[]> {
        const params = new HttpParams().set('objectCode', objectCode);
        return this.http.get<string[]>(`${environment.apiUrl}${this.getAggregatorIoMatrixArchitecturesUrl}`, {params});
    }

    /**
     * The cadence picture for a matrix scope: what the Device IO source is at, which cadences the matrix has
     * saved, and whether the source has moved on and an upgrade is available.
     */
    public getAggregatorIoMatrixCadence(objectCode: string, architecture: string,
                                        powerType: string, keyword: string = ''): Observable<AggregatorIoMatrixCadence> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('architecture', architecture || '')
            .set('powerType', powerType || '')
            .set('keyword', keyword || '');
        return this.http.get<AggregatorIoMatrixCadence>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixCadenceUrl}`, {params});
    }

    /**
     * Returns the target cadence's Device IO rows. The client loads the saved snapshot and merges mappings
     * (same flow as powertrain IO Matrix cadence upgrade).
     */
    public cadenceUpgradeAggregatorIoMatrix(objectCode: string, architecture: string, powerType: string,
                                            targetCadenceVer: string,
                                            keyword: string = ''): Observable<AggregatorIoMatrixRow[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '')
            .set('targetCadenceVer', targetCadenceVer);
        return this.http.post<AggregatorIoMatrixRow[] | AggregatorCadenceUpgrade>(
            `${environment.apiUrl}${this.cadenceUpgradeAggregatorIoMatrixUrl}`, null, {params}).pipe(
            map(response => EsciService.normalizeAggregatorCadenceUpgradeRows(response)));
    }

    /** Accepts either the current array response or the legacy DTO wrapper during API transition. */
    static normalizeAggregatorCadenceUpgradeRows(
        response: AggregatorIoMatrixRow[] | AggregatorCadenceUpgrade | null | undefined
    ): AggregatorIoMatrixRow[] {
        if (Array.isArray(response)) {
            return response;
        }
        if (response && Array.isArray(response.rows)) {
            return response.rows;
        }
        return [];
    }

    public getAggregatorIoMatrixPowerTypes(objectCode: string, architecture: string): Observable<string[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture);
        return this.http.get<string[]>(`${environment.apiUrl}${this.getAggregatorIoMatrixPowerTypesUrl}`, {params});
    }

    public getAggregatorIoMatrixKeywords(objectCode: string, architecture: string,
                                         powerType: string): Observable<string[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('architecture', architecture || '')
            .set('powerType', powerType || '');
        return this.http.get<string[]>(`${environment.apiUrl}${this.getAggregatorIoMatrixKeywordsUrl}`, {params});
    }

    public getAggregatorIoMatrixAggregatorOptions(objectCode: string, architecture: string, powerType: string,
                                                  keyword: string = ''): Observable<AggregatorOption[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.get<AggregatorOption[]>(`${environment.apiUrl}${this.getAggregatorIoMatrixAggregatorOptionsUrl}`, {params});
    }

    public getAggregatorIoMatrixRows(objectCode: string, architecture: string, powerType: string,
                                     cadenceVer: string, keyword: string = ''): Observable<AggregatorIoMatrixLoadResponse> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '')
            .set('cadenceVer', cadenceVer || '');
        return this.http.get<AggregatorIoMatrixLoadResponse>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixRowsUrl}`, {params});
    }

    /**
     * Asks the server to suggest a signal for the rows that have none, by matching the device IO name.
     * The current grid rows are posted so unsaved edits survive; nothing is persisted by this call.
     */
    public autoMatchAggregatorIoMatrix(objectCode: string, architecture: string, powerType: string,
                                       rows: AggregatorIoMatrixRow[],
                                       keyword: string = ''): Observable<AggregatorIoMatrixRow[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.post<AggregatorIoMatrixRow[]>(
            `${environment.apiUrl}${this.autoMatchAggregatorIoMatrixUrl}`, rows || [], {
                params,
                headers: new HttpHeaders({'Content-Type': 'application/json'})
            });
    }

    public getAggregatorIoMatrixAutoMatchRules(): Observable<AggregatorAutoMatchRules> {
        return this.http.get<AggregatorAutoMatchRules>(
            `${environment.apiUrl}${this.autoMatchRulesAggregatorIoMatrixUrl}`);
    }

    public saveAggregatorIoMatrix(objectCode: string, architecture: string, powerType: string,
                                  cadenceVer: string, saveAsNewCadence: boolean, sourceCadenceVer: string,
                                  rows: AggregatorIoMatrixRow[], keyword: string = ''): Observable<any> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '')
            .set('cadenceVer', cadenceVer || '')
            .set('saveAsNewCadence', String(!!saveAsNewCadence))
            .set('sourceCadenceVer', sourceCadenceVer || '');
        return this.http.post(`${environment.apiUrl}${this.saveAggregatorIoMatrixUrl}`, rows || [], {
            params,
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    /**
     * The matrix rows for a scope as an Excel report. Built server-side so a caller that never loaded the
     * rows — the Create Aggregator ICDs page lists matrices, not pins — can still export them.
     */
    public getAggregatorIoMatrixReport(objectCode: string, architecture: string, powerType: string,
                                       cadenceVer?: string, keyword: string = ''): Observable<ArrayBuffer> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('architecture', architecture || '')
            .set('powerType', powerType || '')
            .set('keyword', keyword || '')
            .set('cadenceVer', cadenceVer || '');
        return this.http.get(`${environment.apiUrl}${this.getAggregatorIoMatrixReportUrl}`,
            {params, responseType: 'arraybuffer'});
    }

    public getAggregatorIoMatrixHistory(objectCode: string, architecture: string, powerType: string,
                                        keyword: string = ''): Observable<ArrayBuffer> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '');
        return this.http.get(`${environment.apiUrl}${this.getAggregatorIoMatrixHistoryUrl}`,
                             {params, responseType: 'arraybuffer'});
    }

    public getAggregatorIoMatrixIcdCompare(objectCode: string, architecture: string, powerType: string,
                                           cadenceVer: string, keyword: string = ''): Observable<ArrayBuffer> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('cadenceVer', cadenceVer)
            .set('keyword', keyword || '');
        return this.http.get(`${environment.apiUrl}${this.getAggregatorIoMatrixIcdCompareUrl}`,
                             {params, responseType: 'arraybuffer'});
    }

    public checkAggregatorIoMatrixIcdPermission(): Observable<boolean> {
        return this.http.get<boolean>(`${environment.apiUrl}${this.aggregatorIoMatrixIcdPermissionUrl}`);
    }

    /** The saved Aggregator IO Matrices to pick from - the aggregator stand-in for template inventory. */
    public getAggregatorIoMatrixIcdMatrixInventory(): Observable<AggregatorIoMatrixInventory[]> {
        return this.http.get<AggregatorIoMatrixInventory[]>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixIcdMatrixInventoryUrl}`);
    }

    /**
     * The matrix scope, read through the aggregatorIoMatrixIcd endpoints rather than the matrix page's
     * own. They return the same values, but each is gated by its own role - an AGG_IO_MATRIX_ICD_USER
     * would get a 401 from the matrix page's copies and be unable to pick a scope at all.
     */
    public getAggregatorIoMatrixIcdObjectCodes(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}${this.getAggregatorIoMatrixIcdObjectCodesUrl}`);
    }

    public getAggregatorIoMatrixIcdArchitectures(objectCode: string): Observable<string[]> {
        const params = new HttpParams().set('objectCode', objectCode || '');
        return this.http.get<string[]>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixIcdArchitecturesUrl}`, {params});
    }

    public getAggregatorIoMatrixIcdPowerTypes(objectCode: string, architecture: string): Observable<string[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('architecture', architecture || '');
        return this.http.get<string[]>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixIcdPowerTypesUrl}`, {params});
    }

    public getAggregatorIoMatrixIcdKeywords(objectCode: string, architecture: string,
                                            powerType: string): Observable<string[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('architecture', architecture || '')
            .set('powerType', powerType || '');
        return this.http.get<string[]>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixIcdKeywordsUrl}`, {params});
    }

    public deleteAggregatorIoMatrix(objectCode: string, architecture: string, powerType: string,
                                    cadenceVer: string, keyword: string = ''): Observable<any> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture)
            .set('powerType', powerType)
            .set('keyword', keyword || '')
            .set('cadenceVer', cadenceVer || '');
        return this.http.post(`${environment.apiUrl}${this.deleteAggregatorIoMatrixUrl}`, null, {params});
    }

    public getAggregatorIoMatrixHistoryAll(): Observable<ArrayBuffer> {
        return this.http.get(`${environment.apiUrl}${this.getAggregatorIoMatrixHistoryAllUrl}`,
            {responseType: 'arraybuffer'});
    }

    /**
     * The Aggregator IO Matrix change history for one scope. The Create Aggregator ICDs page uses this
     * for its Export History button now that there is no template inventory to report on.
     */
    public getAggregatorIoMatrixIcdHistoryForScope(objectCode: string, architecture: string,
                                                   powerType: string, keyword: string = ''): Observable<ArrayBuffer> {
        return this.getAggregatorIoMatrixHistory(objectCode, architecture, powerType, keyword);
    }

    public getAggregatorIoMatrixIcdAllowedPrograms(): Observable<AggregatorAllowedProgram[]> {
        return this.http.get<AggregatorAllowedProgram[]>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixIcdAllowedProgramsUrl}`).pipe(
            map(programs => EsciService.distinctSortedAllowedPrograms(programs))
        );
    }







    public getIcdObjectCodes(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}${this.getIcdObjectCodesUrl}`);
    }

    public getIcdObjectCodeInsts(objectCode: string): Observable<string[]> {
        const params = new HttpParams().set('objectCode', objectCode);
        return this.http.get<string[]>(`${environment.apiUrl}${this.getIcdObjectCodeInstsUrl}`, {params});
    }

    public getIcdControllerDesignators(): Observable<string[]> {
        return this.http.get<string[]>(`${environment.apiUrl}${this.getIcdControllerDesignatorsUrl}`);
    }

    public getIcdArchitectures(objectCode: string): Observable<string[]> {
        const params = new HttpParams().set('objectCode', objectCode);
        return this.http.get<string[]>(`${environment.apiUrl}${this.getIcdArchitecturesUrl}`, {params});
    }

    public getIcdRefMapStatus(objectCode: string, objectCodeInst: string): Observable<IcdRefMapStatus | null> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('objectCodeInst', objectCodeInst || '');
        return this.http.get<IcdRefMapStatus | null>(`${environment.apiUrl}${this.getIcdRefMapStatusUrl}`, {params});
    }

    // Aggregator flavour of the ref-map pre-check. The powertrain endpoint gates on
    // POWERTRAIN_IO_MATRIX_ICD_USER, so an AGG_IO_MATRIX_ICD_USER-only user calling it gets a 401 and
    // is blocked from creating ICDs; this hits the aggregator controller, which gates on the agg roles.
    public getAggregatorIoMatrixRefMapStatus(objectCode: string): Observable<IcdRefMapStatus | null> {
        const params = new HttpParams().set('objectCode', objectCode || '');
        return this.http.get<IcdRefMapStatus | null>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixRefMapStatusUrl}`, {params});
    }

    public getAggregatorIoMatrixIcdRefMapStatus(objectCode: string,
                                                objectCodeInst: string): Observable<IcdRefMapStatus | null> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('objectCodeInst', objectCodeInst || '');
        return this.http.get<IcdRefMapStatus | null>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixIcdRefMapStatusUrl}`, {params});
    }

    public saveIoMatrix(objectCode: string, objectCodeInst: string, architecture: string, cadenceVer: string, matrixId: string,
                        rows: IoMatrixRow[], options?: { saveAsNewCadence?: boolean; sourceCadenceVer?: string }): Observable<any> {
        let params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('objectCodeInst', objectCodeInst || '')
            .set('architecture', architecture || '')
            .set('cadenceVer', cadenceVer || '')
            .set('matrixId', matrixId || '');
        if (options?.saveAsNewCadence) {
            params = params
                .set('saveAsNewCadence', 'true')
                .set('sourceCadenceVer', options?.sourceCadenceVer || '');
        }
        return this.http.post(`${environment.apiUrl}${this.saveIoMatrixUrl}`, rows || [], {
            params,
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    public triggerIoMatrixCadenceUpgrade(objectCode: string, objectCodeInst: string, architecture: string,
                                         targetCadenceVer: string): Observable<IoMatrixRow[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('objectCodeInst', objectCodeInst || '')
            .set('architecture', architecture || '')
            .set('targetCadenceVer', targetCadenceVer || '');
        return this.http.post<IoMatrixRow[]>(`${environment.apiUrl}${this.cadenceUpgradeUrl}`, null, {
            params,
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    public getIcdTemplateCircuitOptions(objectCode: string, architecture: string): Observable<string[]> {
        const params = new HttpParams()
            .set('objectCode', objectCode)
            .set('architecture', architecture);
        return this.http.get<string[]>(`${environment.apiUrl}${this.getIcdTemplateCircuitOptionsUrl}`, {params});
    }

    public getIcdCircuitSearchResults(criteria: {
        circNO?: string;
        circSig?: string;
        circSubsys?: string;
        circColor?: string;
        circDesc?: string;
        limit?: number;
    }): Observable<any[]> {
        let params = new HttpParams();
        if (criteria?.circNO) {
            params = params.set('circNO', criteria.circNO);
        }
        if (criteria?.circSig) {
            params = params.set('circSig', criteria.circSig);
        }
        if (criteria?.circSubsys) {
            params = params.set('circSubsys', criteria.circSubsys);
        }
        if (criteria?.circColor) {
            params = params.set('circColor', criteria.circColor);
        }
        if (criteria?.circDesc) {
            params = params.set('circDesc', criteria.circDesc);
        }
        params = params.set('limit', String(criteria?.limit || 100));
        return this.http.get<any[]>(`${environment.apiUrl}${this.getIcdCircuitSearchUrl}`, {params});
    }

    public saveIcdTemplateValues(objectCode: string, objectCodeInst: string, architecture: string, cadenceVer: string,
                                 program: string, modelYear: string, rpo: string, additionalRpos: string, buildPhase: string,
                                 variant: string,
                                 rows: { fkIoMatrixRowId: number; icdCircuitSelection: string }[]): Observable<any> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('objectCodeInst', objectCodeInst || '')
            .set('architecture', architecture || '')
            .set('cadenceVer', cadenceVer || '')
            .set('program', program || '')
            .set('modelYear', modelYear || '')
            .set('rpo', rpo || '')
            .set('additionalRpos', additionalRpos || '')
            .set('buildPhase', buildPhase || '')
            .set('variant', variant || '');
        return this.http.post(`${environment.apiUrl}${this.saveIcdTemplateValuesUrl}`, rows || [], {
            params,
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    public startFreshIcdTemplate(objectCode: string, objectCodeInst: string, architecture: string, cadenceVer: string,
                                 program: string, modelYear: string, rpo: string, additionalRpos: string,
                                 buildPhase: string, variant: string): Observable<any> {
        const params = new HttpParams()
            .set('objectCode', objectCode || '')
            .set('objectCodeInst', objectCodeInst || '')
            .set('architecture', architecture || '')
            .set('cadenceVer', cadenceVer || '')
            .set('program', program || '')
            .set('modelYear', modelYear || '')
            .set('rpo', rpo || '')
            .set('additionalRpos', additionalRpos || '')
            .set('buildPhase', buildPhase || '')
            .set('variant', variant || '');
        return this.http.post(`${environment.apiUrl}${this.startFreshIcdTemplateUrl}`, null, {
            params,
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    public getIcdTemplateInventory(): Observable<IcdTemplateInventory[]> {
        return this.http.get<IcdTemplateInventory[]>(`${environment.apiUrl}${this.getIcdTemplateInventoryUrl}`);
    }

    public getIcdTemplateDetail(templateId: number): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}${this.getIcdTemplateDetailUrl}/${templateId}/detail`);
    }

    public createIcdsFromTemplates(templateIds: number[],
                                   metadata: Record<string, string | boolean>): Observable<CreateIcdsFromTemplatesResponse> {
        return this.postCreateIcdsFromTemplates(this.createIcdsFromTemplateUrl, templateIds, metadata);
    }

    /**
     * Creates one ICD from an Aggregator IO Matrix. There is no template step for aggregators - every
     * matrix row is always included - so the scope and the ICD identity are posted straight from the
     * form instead of being read off a saved template. The scope includes the saved cadence snapshot the
     * user selected, so the ICD is built from the rows they reviewed rather than from whichever cadence is
     * latest when the request arrives.
     */
    public createAggregatorIcd(metadata: Record<string, string | boolean>): Observable<CreateIcdsFromTemplatesResponse> {
        const metadataRecord = metadata || {};
        return this.http.post<CreateIcdsFromTemplatesResponse>(
            `${environment.apiUrl}${this.createAggregatorIcdUrl}`, {
                objectCode: this.getStringMetadataValue(metadataRecord, 'objectCode'),
                architecture: this.getStringMetadataValue(metadataRecord, 'architecture'),
                powerType: this.getStringMetadataValue(metadataRecord, 'powerType'),
                savedCadenceVer: this.getStringMetadataValue(metadataRecord, 'savedCadenceVer'),
                program: this.getStringMetadataValue(metadataRecord, 'program'),
                modelYear: this.getStringMetadataValue(metadataRecord, 'modelYear'),
                rpo: this.getStringMetadataValue(metadataRecord, 'rpo'),
                additionalRpos: this.getStringMetadataValue(metadataRecord, 'additionalRpos'),
                buildPhase: this.getStringMetadataValue(metadataRecord, 'buildPhase'),
                keyword: this.getStringMetadataValue(metadataRecord, 'keyword'),
                ...this.buildCreateIcdPayload(metadataRecord)
            }, {
                headers: new HttpHeaders({'Content-Type': 'application/json'})
            });
    }

    private postCreateIcdsFromTemplates(createUrl: string,
                                        templateIds: number[],
                                        metadata: Record<string, string | boolean>): Observable<CreateIcdsFromTemplatesResponse> {
        const metadataRecord = metadata || {};
        return this.http.post<CreateIcdsFromTemplatesResponse>(`${environment.apiUrl}${createUrl}`, {
            templateIds: templateIds || [],
            ...this.buildCreateIcdPayload(metadataRecord)
        }, {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    /** The ICD attributes both create flows send; only the ICD's identity source differs between them. */
    private buildCreateIcdPayload(metadata: Record<string, string | boolean>): Record<string, unknown> {
        const metadataRecord = metadata || {};
        return {
            metadata: this.toIcdMetadataPayload(metadataRecord),
            icdType: this.getStringMetadataValue(metadataRecord, 'icdType') || 'Vehicle',
            icdSubType: this.getStringMetadataValue(metadataRecord, 'icdSubType') || 'NA',
            deviceVariant: this.getStringMetadataValue(metadataRecord, 'deviceVariant') || '01',
            deviceVariantDesc: this.getStringMetadataValue(metadataRecord, 'deviceVariantDesc'),
            childObjectCodes: this.getStringMetadataValue(metadataRecord, 'childObjectCodes'),
            revision: Number(this.getStringMetadataValue(metadataRecord, 'revision') || '1'),
            objectCodeInst: this.getStringMetadataValue(metadataRecord, 'objectCodeInst'),
            parentIndicator: this.getBooleanMetadataValue(metadataRecord, 'parentIcd'),
            vpps: this.getStringMetadataValue(metadataRecord, 'vpps'),
            upcCode: this.getStringMetadataValue(metadataRecord, 'upc'),
            fnaCode: this.getStringMetadataValue(metadataRecord, 'fna'),
            icdSourced: this.getStringMetadataValue(metadataRecord, 'icdSourced'),
            icdSourcedDate: this.getDateMetadataValue(metadataRecord, 'icdSourcedDate')
        };
    }

    public getCreateIcdFormOptions(): Observable<CreateIcdFormOptionsResponse> {
        return this.http.get<CreateIcdFormOptionsResponse>(`${environment.apiUrl}${this.getCreateIcdFormOptionsUrl}`);
    }

    public getAggregatorIoMatrixIcdCreateIcdFormOptions(): Observable<CreateIcdFormOptionsResponse> {
        return this.http.get<CreateIcdFormOptionsResponse>(
            `${environment.apiUrl}${this.getAggregatorIoMatrixIcdCreateIcdFormOptionsUrl}`);
    }

    private toIcdMetadataPayload(metadata: Record<string, string | boolean>): Record<string, unknown> {
        return {
            spLocation: this.getStringMetadataValue(metadata, 'spLocation'),
            checkStatus: {checkStatus: this.getStringMetadataValue(metadata, 'cadence')},
            buildStatus: {buildStatus: this.getStringMetadataValue(metadata, 'buildStatus')},
            platform: this.getStringMetadataValue(metadata, 'platform') || this.getStringMetadataValue(metadata, 'architecture'),
            classifier: {classifier: this.getStringMetadataValue(metadata, 'securityClassifier')},
            diodeIFSM: this.getNumberMetadataValue(metadata, 'diodeIFSM'),
            diodeVRRM: this.getNumberMetadataValue(metadata, 'diodeVRRM'),
            operateNormally: this.getStringMetadataValue(metadata, 'operateNormally'),
            fullyFunctional: this.getStringMetadataValue(metadata, 'fullyFunctional'),
            dreUsers: {
                userA: {gmin: this.getStringMetadataValue(metadata, 'deviceDre')},
                userB: {gmin: this.getStringMetadataValue(metadata, 'electricalArchitect')}
            },
            vseUsers: {
                userA: {gmin: this.getStringMetadataValue(metadata, 'deviceVse')},
                userB: {gmin: this.getStringMetadataValue(metadata, 'electricalVse')}
            },
            egmUsers: {
                userA: {gmin: this.getStringMetadataValue(metadata, 'deviceEgm')},
                userB: {gmin: this.getStringMetadataValue(metadata, 'architectEgm')}
            },
            dreDelegateUsers: {
                userA: {gmin: this.getStringMetadataValue(metadata, 'delegateDeviceDre')},
                userB: {gmin: this.getStringMetadataValue(metadata, 'delegateElectricalArchitect')}
            },
            delegateDreUserARequired: this.getBooleanMetadataValue(metadata, 'delegateDeviceDreApprovalRequired'),
            delegateDreUserBRequired: this.getBooleanMetadataValue(metadata, 'delegateElectricalArchitectApprovalRequired'),
            icdEngineer: {gmin: this.getStringMetadataValue(metadata, 'icdeEngineer')},
            rcdInterfIdentifiedDate: this.getDateMetadataValue(metadata, 'rcdInterfIdentifiedDate'),
            rcdConnDate: this.getDateMetadataValue(metadata, 'rcdConnDate'),
            rcdInterfAgreedDate: this.getDateMetadataValue(metadata, 'rcdInterfAgreedDate'),
            rcdIverDate: this.getDateMetadataValue(metadata, 'rcdIverDate'),
            rcdSopDate: this.getDateMetadataValue(metadata, 'rcdSopDate'),
            icdEmailPref: this.getEmailPreferencePayload(metadata, 'ICD', 'allUsersEmailStatus', 'allUsersEmail'),
            icdEngrEmailPref: this.getEmailPreferencePayload(metadata, 'ICD_ENGR', 'icdeEngineerEmailStatus', 'icdeEngineerEmail'),
            dreAEmailPref: this.getEmailPreferencePayload(metadata, 'DRE_A', 'deviceDreEmailStatus', 'deviceDreEmail'),
            dreBEmailPref: this.getEmailPreferencePayload(metadata, 'DRE_B', 'electricalArchitectEmailStatus', 'electricalArchitectEmail'),
            vseAEmailPref: this.getEmailPreferencePayload(metadata, 'VSE_A', 'deviceVseEmailStatus', 'deviceVseEmail'),
            vseBEmailPref: this.getEmailPreferencePayload(metadata, 'VSE_B', 'electricalVseEmailStatus', 'electricalVseEmail'),
            egmAEmailPref: this.getEmailPreferencePayload(metadata, 'EGM_A', 'deviceEgmEmailStatus', 'deviceEgmEmail'),
            egmBEmailPref: this.getEmailPreferencePayload(metadata, 'EGM_B', 'architectEgmEmailStatus', 'architectEgmEmail')
        };
    }

    private getEmailPreferencePayload(
        metadata: Record<string, string | boolean>,
        type: string,
        statusKey: string,
        emailKey: string
    ): Record<string, string> {
        return {
            type,
            emailStat: this.normalizeEmailPreferenceStatus(this.getStringMetadataValue(metadata, statusKey)),
            email: this.getStringMetadataValue(metadata, emailKey)
        };
    }

    private normalizeEmailPreferenceStatus(value: string): string {
        return value?.toUpperCase() === 'OFF' ? 'OFF' : 'ON';
    }

    private getStringMetadataValue(metadata: Record<string, string | boolean>, key: string): string {
        const value = metadata[key];
        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }
        return value || '';
    }

    private getBooleanMetadataValue(metadata: Record<string, string | boolean>, key: string): boolean {
        const value = metadata[key];
        if (typeof value === 'boolean') {
            return value;
        }
        return value === 'true';
    }

    private getNumberMetadataValue(metadata: Record<string, string | boolean>, key: string): number | null {
        const value = this.getStringMetadataValue(metadata, key);
        if (!value) {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private getDateMetadataValue(metadata: Record<string, string | boolean>, key: string): string | null {
        const value = this.getStringMetadataValue(metadata, key);
        return value || null;
    }

    public getIoMatrixHistory(matrixId: string, cadenceVer?: string): Observable<ArrayBuffer> {
        let params = new HttpParams().set('matrixId', matrixId);
        if (cadenceVer) {
            params = params.set('cadenceVer', cadenceVer);
        }
        return this.http.get(`${environment.apiUrl}${this.getIoMatrixHistoryUrl}`,
                             {params, responseType: 'arraybuffer'});
    }

    public getIcdTemplateHistory(objectCode: string, objectCodeInst: string,
                                 architecture: string, cadenceVer?: string): Observable<ArrayBuffer> {
        let params = new HttpParams()
            .set('objectCode', objectCode)
            .set('objectCodeInst', objectCodeInst)
            .set('architecture', architecture);
        if (cadenceVer) {
            params = params.set('cadenceVer', cadenceVer);
        }
        return this.http.get(`${environment.apiUrl}${this.getIcdTemplateHistoryUrl}`,
                             {params, responseType: 'arraybuffer'});
    }

    public getIoMatrixIcdTemplateHistory(templateIds: number[]): Observable<ArrayBuffer> {
        return this.http.post(`${environment.apiUrl}${this.getIoMatrixIcdTemplateHistoryUrl}`,
            templateIds || [],
            {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'arraybuffer'});
    }

    public getIoMatrixIcdTemplateReport(templateIds: number[]): Observable<ArrayBuffer> {
        return this.http.post(`${environment.apiUrl}${this.getIoMatrixIcdTemplateReportUrl}`,
            templateIds || [],
            {headers: new HttpHeaders({'Content-Type': 'application/json'}), responseType: 'arraybuffer'});
    }

    public createReleaseSchedule(schedule: ReleaseSchedule): Observable<ReleaseSchedule> {
        return this.http.post<ReleaseSchedule>(`${environment.apiUrl}${this.getReleaseSchedulesUrl}`, schedule);
    }

    public startReleaseSchedule(id: number): Observable<any> {
        return this.http.post(`${environment.apiUrl}${this.getReleaseSchedulesUrl}/${id}/start`, {});
    }

    public finishReleaseSchedule(id: number): Observable<any> {
        return this.http.post(`${environment.apiUrl}${this.getReleaseSchedulesUrl}/${id}/finish`, {});
    }

    public cancelReleaseSchedule(id: number): Observable<any> {
        return this.http.post(`${environment.apiUrl}${this.getReleaseSchedulesUrl}/${id}/cancel`, {});
    }

    // --- ICD Subscriptions ("My ICD Subscriptions" page) ---

    public getIcdSubscriptions(): Observable<IcdSubscriptionListItem[]> {
        return this.http.get<IcdSubscriptionListItem[]>(`${environment.apiUrl}${this.getIcdSubscriptionsUrl}`);
    }

    public addIcdSubscription(icdId: number): Observable<boolean> {
        return this.http.post<boolean>(`${environment.apiUrl}${this.addIcdSubscriptionUrl}/${icdId}`, {});
    }

    public removeIcdSubscription(icdId: number): Observable<boolean> {
        return this.http.delete<boolean>(`${environment.apiUrl}${this.addIcdSubscriptionUrl}/${icdId}`);
    }

    public addIcdSubscriptionGroup(programId: number, modelYearId: number): Observable<boolean> {
        const params = new HttpParams().set('programId', programId).set('modelYearId', modelYearId);
        return this.http.post<boolean>(`${environment.apiUrl}${this.addIcdSubscriptionGroupUrl}`, {}, {params});
    }

    public removeIcdSubscriptionGroup(programId: number, modelYearId: number): Observable<boolean> {
        const params = new HttpParams().set('programId', programId).set('modelYearId', modelYearId);
        return this.http.delete<boolean>(`${environment.apiUrl}${this.addIcdSubscriptionGroupUrl}`, {params});
    }

    public getIcdSubscriptionGroupPreviewCount(programId: number, modelYearId: number): Observable<number> {
        const params = new HttpParams().set('programId', programId).set('modelYearId', modelYearId);
        return this.http.get<number>(`${environment.apiUrl}${this.getIcdSubscriptionGroupPreviewCountUrl}`, {params});
    }

    public getIcdSubscriptionProgramOptions(): Observable<IcdSubscriptionProgramOption[]> {
        return this.http.get<IcdSubscriptionProgramOption[]>(
            `${environment.apiUrl}${this.getIcdSubscriptionProgramOptionsUrl}`);
    }

    public getIcdSubscriptionModelYearOptions(): Observable<IcdSubscriptionModelYearOption[]> {
        return this.http.get<IcdSubscriptionModelYearOption[]>(
            `${environment.apiUrl}${this.getIcdSubscriptionModelYearOptionsUrl}`);
    }

    public getPermissionAndFileSizeLimit(component: EsciComponent, checkFileSize: boolean = true) {
        const requestGeneration = (this.permissionRequestGenerations.get(component) ?? 0) + 1;
        this.permissionRequestGenerations.set(component, requestGeneration);
        const isCurrentRequest = () => this.permissionRequestGenerations.get(component) === requestGeneration;
        let outstandingPermissionChecks = 4;
        const permissionCheckSettled = () => {
            if (!isCurrentRequest()) {
                return;
            }
            outstandingPermissionChecks--;
            if (outstandingPermissionChecks === 0) {
                component.onAccessPermissionsResolved?.();
            }
        };
        this.checkPermission(component, permissionCheckSettled, isCurrentRequest);
        if (checkFileSize) {
            this.getFileSizeLimit(component);
        }
        // Admin permission url is undefined by default
        // if a component has one, this will run and set the permission boolean true if true
        // if not, this will set the boolean to true as there is only one level of permission
        this.checkAdminPermission(component, permissionCheckSettled, isCurrentRequest);
        // Also check for PURCH_ADMIN permission (for PPCO editing)
        this.checkPurchAdminPermission(component, permissionCheckSettled, isCurrentRequest);
        // And COMPONENT_TIER_N_ADMIN permission for Tier-N Resilient editing
        this.checkTierNAdminPermission(component, permissionCheckSettled, isCurrentRequest);
    }

    private setPermission(component: EsciComponent, value: boolean): void {
        component.permission.set(value);
    }

    private setAdminPermission(component: EsciComponent, value: boolean): void {
        component.adminPermission.set(value);
    }

    public checkPermission(component: EsciComponent, onSettled?: () => void, isCurrentRequest = () => true) {
        this.setPermission(component, false);
        this.makeServiceCall(component.permissionUrl).pipe(
            finalize(() => onSettled?.())
        ).subscribe({
            next: (event: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                if (event instanceof HttpResponse) {
                    if (event.body === true) {
                        console.log('User has permission to use tool.');
                        this.setPermission(component, true);
                    } else {
                        console.log('User does not have permission to use the tool.');
                        component.messageService.processError('User does not have permission to use the tool.');
                        this.setPermission(component, false);
                    }
                }
            },
            error: (err: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                console.log(err);
                component.messageService.processError('Unable to reach eSCI to determine if user has permission to use this tool.');
                this.setPermission(component, false);
            }
        });
    }

    public checkAdminPermission(component: EsciComponent, onSettled?: () => void, isCurrentRequest = () => true) {
        this.setAdminPermission(component, false);
        if (!component.adminPermissionUrl) {
            if (isCurrentRequest()) {
                this.setAdminPermission(component, true);
                component.onAdminPermissionResolved?.();
            }
            onSettled?.();
            return;
        }
        this.makeServiceCall(component.adminPermissionUrl).pipe(
            finalize(() => {
                if (isCurrentRequest()) {
                    component.onAdminPermissionResolved?.();
                }
                onSettled?.();
            })
        ).subscribe({
            next: (event: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                if (event instanceof HttpResponse) {
                    if (event.body === true) {
                        console.log('User has admin permission to use tool.');
                        this.setAdminPermission(component, true);
                    } else {
                        console.log('User does not have admin permission to use the tool.');
                        this.setAdminPermission(component, false);
                    }
                }
            },
            error: (err: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                console.log(err);
                component.messageService.processError('Unable to reach eSCI to determine if user has admin permission to use this tool.');
                this.setAdminPermission(component, false);
            }
        });
    }

    public checkPurchAdminPermission(component: EsciComponent, onSettled?: () => void, isCurrentRequest = () => true) {
        component.hasPurchAdminPermission.set(false);
        this.makeServiceCall('/esciUI/purchAdminPermission').pipe(
            finalize(() => onSettled?.())
        ).subscribe({
            next: (event: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                if (event instanceof HttpResponse) {
                    if (event.body === true) {
                        console.log('User has PURCH_ADMIN permission.');
                        component.hasPurchAdminPermission.set(true);
                    } else {
                        console.log('User does not have PURCH_ADMIN permission.');
                    }
                }
            },
            error: (err: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                console.log(err);
                // Don't show error message, this is an optional permission
            }
        });
    }

    /**
     * Asks the backend whether the calling user has the COMPONENT_TIER_N_ADMIN role and
     * stores the answer on the component's hasTierNAdminPermission signal. Mirrors
     * checkPurchAdminPermission. Used by the grid and detail views to decide whether
     * Tier-N Resilient fields should be editable and whether to surface the
     * "Save Tier-N Only" action.
     */
    public checkTierNAdminPermission(component: EsciComponent, onSettled?: () => void, isCurrentRequest = () => true) {
        component.hasTierNAdminPermission.set(false);
        this.makeServiceCall('/esciUI/tierNAdminPermission').pipe(
            finalize(() => onSettled?.())
        ).subscribe({
            next: (event: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                if (event instanceof HttpResponse) {
                    if (event.body === true) {
                        console.log('User has COMPONENT_TIER_N_ADMIN permission.');
                        component.hasTierNAdminPermission.set(true);
                    } else {
                        console.log('User does not have COMPONENT_TIER_N_ADMIN permission.');
                    }
                }
            },
            error: (err: any) => {
                if (!isCurrentRequest()) {
                    return;
                }
                console.log(err);
                // Don't show error message, this is an optional permission
            }
        });
    }

    public checkExportPermission(component: EsciComponent) {
        component.canExport.set(false);
        this.makeServiceCall('/esciUI/exportPermission').subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    component.canExport.set(event.body === true);
                    console.log('User export permission:', component.canExport());
                }
            },
            error: (err: any) => {
                console.log('Unable to determine export permission:', err);
                component.canExport.set(false); // already set to false on method entry, but extra safety is good.
            }
        });
    }

    public getFileSizeLimit(component: EsciComponent) {
        this.getFileSizeLimitByUrl(component, this.buildCheckToolFileSizeUrl);
    }

    public getFileSizeLimitByUrl(component: EsciComponent, url: string) {
        this.makeServiceCall(url).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    let fileSizeStr = event.body;
                    // Backend returns JSON string, parse it if needed
                    if (typeof fileSizeStr === 'string' && fileSizeStr.startsWith('"') && fileSizeStr.endsWith('"')) {
                        fileSizeStr = JSON.parse(fileSizeStr);
                    }
                    if (fileSizeStr && fileSizeStr.length > 0) {
                        component.MAX_FILE_SIZE = parseInt(fileSizeStr, 10);
                        console.log('File size limit loaded from backend:', component.MAX_FILE_SIZE);
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get file size limit from eSCI. Please try again later.');
            }
        });
    }

    public fetchReferenceData(component: EsciComponent, listName: string, list: Array<string>, url: string, completionCallback?: () => void) {
        const storedVals = sessionStorage.getItem(listName);
        // We want to overwrite the list with the results from either the cache or the URL lookup, so we clear the list:
        list.length = 0;
        if (storedVals) {
            list.push(...JSON.parse(storedVals));
            if (completionCallback) {
                completionCallback();
            }
        } else {
            this.makeServiceCall(url).subscribe({
                next: (event: any) => {
                    if (event instanceof HttpResponse) {
                        if (event.body && event.body.length > 0) {
                            list.push(...event.body);
                            sessionStorage.setItem(listName, JSON.stringify(event.body));
                        }
                    }
                },
                error: (err: any) => {
                    console.log(err);
                    // This should never happen. This means the code is broken or esci is down
                    component.messageService.processError('Failed to get '
                        + listName + ' from eSCI. Please try again later.');
                },
                complete: () => {
                    if (completionCallback) {
                        completionCallback();
                    }
                }
            });
        }
    }

    public getMomentumProgramList(component: KtsComponent) {
        component.momentumProgramMap.clear();
        component.modelYears.length = 0;
        component.setLoadingMomentumData(true);
        this.getMomentumData(component, new HttpParams(), this.ktsMomentumProgramListUrl, false);
    }

    public getKtsEcsMomentumPrograms(component: KtsComponent) {
        component.ecsMomentumPrograms.set([]);
        component.setLoadingMomentumData(true);
        this.makeServiceCall(this.ktsEcsMomentumProgramsUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse && event.body) {
                    const loaded: MomentumProgram[] = [];
                    event.body.forEach((program: any) => {
                        const mp = new MomentumProgram();
                        mp.setDetails(program);
                        loaded.push(mp);
                    });
                    component.ecsMomentumPrograms.set(loaded);
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError(
                    'Failed to get ECS GPS Momentum programs from eSCI. Please try again later.');
                component.setLoadingMomentumData(false);
            },
            complete: () => {
                component.setLoadingMomentumData(false);
                component.resetEcsSelection();
            }
        });
    }

    public getMomentumProgramByMomentumId(component: KtsComponent) {
        const params = new HttpParams().set('momentumId', component.selectedMomentumProgram.momentumId);
        this.getMomentumData(component, params, this.ktsMomentumProgramByMomentumIdUrl, true);
    }

    // this method is used to get all momentum programs or specific timing by id
    public getMomentumData(component: KtsComponent, params: HttpParams, url: string, timingDetails: boolean) {
        this.makeServiceCallWithParams(url, params).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    const modelYearsArray = new Array<ModelYear>();
                    if (event.body && event.body.length > 0) {
                        event.body.map(program => {
                            // Skip any momentum programs that do not have a model year
                            if (program.MODEL_YEAR) {
                                const mp = new MomentumProgram();
                                mp.momentumId = program.PROGRAM_ID;
                                mp.programName = program.PROGRAM_NAME;
                                mp.modelYear = program.MODEL_YEAR;
                                // if true, we are only getting one program with full details
                                if (timingDetails) {
                                    // Make sure this is a unique program for our list
                                    const programKey = MomentumProgram.getProgramKey(mp, component.type);
                                    if (!component.allowDuplicates && component.selectedMomentumProgramList.some(
                                        p => MomentumProgram.getProgramKey(p, component.type) === programKey)) {
                                        component.messageService.processError('You have already selected this program. ' +
                                            'Please choose a different unique program.');
                                        component.setLoadingMomentumData(false);
                                        return;
                                    }
                                    mp.setDetails(program);
                                    component.selectedMomentumProgram = mp;
                                    component.selectedMomentumProgramList.push(mp);
                                    component.syncSelectedMomentumProgramState();
                                    component.calculateKtsTimingWithProgram();
                                } else {
                                    // if false, we are getting a large list but without details
                                    const modelYear = mp.modelYear;
                                    if (!component.momentumProgramMap.has(modelYear)) {
                                        component.momentumProgramMap.set(modelYear, new Array<MomentumProgram>());
                                    }
                                    component.momentumProgramMap.get(modelYear).push(mp);
                                    // set model years list
                                    if (modelYearsArray.findIndex(m => m.year === modelYear) === -1) {
                                        modelYearsArray.push(new ModelYear(modelYear));
                                    }
                                }
                            }
                        });
                    } else {
                        if (timingDetails) {
                            component.messageService.processError('Did not find Momentum Data for that program.');
                        }
                    }
                    if (!timingDetails) {
                        // sort model years
                        modelYearsArray.sort((a, b) => a.year.localeCompare(b.year));
                        component.modelYears = modelYearsArray;
                        // sort momentumProgramMap
                        component.momentumProgramMap.forEach((list, year: string) => {
                            list.sort((a, b) => a.programName.localeCompare(b.programName));
                        });
                        // Set model year to current year, using reference from modelYears array if available
                        const currentYear = new Date().getFullYear().toString();
                        const foundModelYear = component.modelYears.find(m => m.year === currentYear);
                        component.selectedModelYear = foundModelYear ? foundModelYear : new ModelYear(currentYear);
                    }
                    component.setLoadingMomentumData(false);
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Momentum data from eSCI. Please try again later.');
                component.setLoadingMomentumData(false);
            }
        });
    }

    public getKtsTimingTemplates(component: KtsComponent, useMinorVersion?: boolean, p?: () => void) {
        let type = 'PROD';
        component.ktsTimingTemplateRowMap = new Map<string, Map<string, Array<KtsTimingTemplateRow>>>();
        // Let's get all available template versions and put them into our map
        this.makeServiceCall(component.templateUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length) {
                        event.body.map(result => {
                            const row = new KtsTimingTemplateRow(result, useMinorVersion);
                            // use row version as key and put in map if not there already
                            const version = row.version;
                            type = row.type;
                            row.originalRowData = structuredClone(row);
                            if (!component.ktsTimingTemplateRowMap.get(type)) {
                                component.ktsTimingTemplateRowMap.set(type, new Map<string, Array<KtsTimingTemplateRow>>());
                            }
                            if (!component.ktsTimingTemplateRowMap.get(type).has(version)) {
                                component.ktsTimingTemplateRowMap.get(type).set(version, new Array<KtsTimingTemplateRow>());
                            }
                            component.ktsTimingTemplateRowMap.get(type).get(version).push(row);
                        });
                        for (const type2 of component.ktsTimingTemplateRowMap.keys()) {
                            // Sort the ktsTimingTemplateRows by row order for each version in the map
                            component.ktsTimingTemplateRowMap.get(type2).forEach((value, key) => {
                                value.sort((a, b) => {
                                    const rowComparison = a.rowNumber - b.rowNumber;
                                    if (rowComparison !== 0) {
                                        return rowComparison;
                                    }
                                    // If rowNumber is the same (old bug, bad data?), sort by deliverable
                                    return a.deliverable.localeCompare(b.deliverable);
                                });
                            });
                            // sort the map by the key values
                            component.ktsTimingTemplateRowMap.set(type2, new Map([...component.ktsTimingTemplateRowMap.get(type2).entries()].sort(([a], [b]) => a.localeCompare(b))));
                            // add columnDefs to the fullCols map for each version in the template map
                            component.ktsTimingTemplateRowMap.get(type2).forEach((value, key) => {
                                // continue if the version status is archived. this way in the editor
                                // we do not have tabs or grids for the archived ones
                                // for the non-editor, we only get Active ones anyways
                                if (component.getStatusOfVersion(key, type2) !== 'ARCHIVED') {
                                    if (!component.fullCols.get(type2)) {
                                        component.fullCols.set(type2, new Map<string, any>());
                                    }
                                    component.fullCols.get(type2).set(key, structuredClone(component.cols));
                                }
                            });
                        }
                        component.syncKtsTimingTemplateRowMapState();
                        // set the active tab from the type the component is actually showing - `type` above is
                        // reassigned per row, so with three template types it holds whichever type came last
                        if (!component.version) {
                            const versionsForSelectedType = component.fullCols.get(component.type)
                                ?? component.fullCols.get(type);
                            if (versionsForSelectedType?.size) {
                                component.handleTabClick(versionsForSelectedType.keys().next().value);
                            }
                        }
                    } else {
                        component.messageService.processError('Failed to get Timing Template data from eSCI. Please try again later.');
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Timing Template data from eSCI. Please try again later.');
            },
            complete: () => {
                if (p) {
                    p();
                }
            }
        });
    }

    /**
     * After a template save, reload only version/type pairs present in the save payload instead of
     * GET ktsTimingTemplatesForEditor (entire table). On failure, falls back to a full reload.
     */
    public refreshKtsTimingTemplateVersionsAfterSave(
        component: KtsComponent,
        savedRows: ValidationModel[],
        useMinorVersion: boolean | undefined,
        onComplete: () => void
    ): void {
        const targets = this.collectDistinctKtsTemplateVersionTargets(savedRows);
        if (targets.length === 0) {
            onComplete();
            return;
        }
        forkJoin(
            targets.map(({type, version}) =>
                this.http.get<KtsTimingTemplateRow[]>(`${environment.apiUrl}${this.getKtsTimingTemplateRowsForEditorUrl}`, {
                    params: new HttpParams().set('type', type).set('version', version),
                    observe: 'response',
                })
            )
        ).subscribe({
            next: (responses) => {
                for (let i = 0; i < responses.length; i++) {
                    const res = responses[i];
                    const {type, version} = targets[i];
                    const body = res.body;
                    if (!body?.length) {
                        component.ktsTimingTemplateRowMap.get(type)?.delete(version);
                        component.fullCols.get(type)?.delete(version);
                        component.gridApiMap.delete(version);
                        continue;
                    }
                    const rows = body.map((result: any) => {
                        const row = new KtsTimingTemplateRow(result, useMinorVersion);
                        row.originalRowData = structuredClone(row);
                        return row;
                    });
                    rows.sort((a, b) => {
                        const rowComparison = a.rowNumber - b.rowNumber;
                        if (rowComparison !== 0) {
                            return rowComparison;
                        }
                        return a.deliverable.localeCompare(b.deliverable);
                    });
                    if (!component.ktsTimingTemplateRowMap.get(type)) {
                        component.ktsTimingTemplateRowMap.set(type, new Map());
                    }
                    const typeMap = component.ktsTimingTemplateRowMap.get(type)!;
                    typeMap.set(version, rows);
                    component.ktsTimingTemplateRowMap.set(type, new Map([...typeMap.entries()]
                        .sort(([a], [b]) => a.localeCompare(b))));
                    const status = component.getStatusOfVersion(version, type);
                    if (status === 'ARCHIVED') {
                        component.fullCols.get(type)?.delete(version);
                    } else {
                        if (!component.fullCols.get(type)) {
                            component.fullCols.set(type, new Map());
                        }
                        component.fullCols.get(type)!.set(version, structuredClone(component.cols));
                    }
                }
                component.syncKtsTimingTemplateRowMapState();
            },
            error: (err: unknown) => {
                console.log(err);
                this.getKtsTimingTemplates(component, useMinorVersion, onComplete);
            },
            complete: () => {
                onComplete();
            },
        });
    }

    private collectDistinctKtsTemplateVersionTargets(rows: ValidationModel[]): { type: string; version: string }[] {
        const seen = new Set<string>();
        const out: { type: string; version: string }[] = [];
        for (const m of rows) {
            const row = m as KtsTimingTemplateRow;
            const type = row.type?.trim();
            const version = (row.fullVersion || row.version)?.trim();
            if (!type || !version) {
                continue;
            }
            const key = `${type}|${version}`;
            if (!seen.has(key)) {
                seen.add(key);
                out.push({type, version});
            }
        }
        return out;
    }

    public getProgramRuns(component: KtsComponent, programName: string, getIcdsNotOnSchedule?: boolean,
                          includeArchived?: boolean,
                          p?: (component: KtsComponent, programName: string, programRun: ProgramRun) => void,
                          momentumIds?: string[]): Map<string, ProgramRun[]> {
        const programRuns = new Map<string, Array<ProgramRun>>();
        component.programRunsLookupFailed = false;
        let params = new HttpParams();
        if (programName) {
            params = params.set('programName', programName);
        }
        if (momentumIds?.length) {
            params = params.set('momentumIds', momentumIds.join(','));
        }
        if (getIcdsNotOnSchedule) {
            params = params.set('getIcdsNotOnSchedule', getIcdsNotOnSchedule);
        }
        if (includeArchived) {
            params = params.set('includeArchived', includeArchived);
        }
        this.makeServiceCallWithParams(this.getKtsProgramRunsUrl, params).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        event.body.forEach((result: any) => {
                            const pr = new ProgramRun();
                            pr.setProgramRunFromEsciData(result);
                            const type = pr.type ?? pr.programTimingRows?.[0]?.ktsTimingTemplateRow?.type;
                            if (!type) {
                                return;
                            }
                            if (!programRuns.has(type)) {
                                programRuns.set(type, new Array<ProgramRun>());
                            }
                            programRuns.get(type).push(pr);
                        });
                        if (p && programRuns.get(component.type) && programRuns.get(component.type).length) {
                            const selected = component.pendingEcsSelectedProgram || component.selectedMomentumProgram;
                            const expectedKey = component.type === 'ECS' && selected
                                ? `${MomentumProgram.getProgramKey(selected, component.type)}|${component.type}`
                                : programName;
                            const matchingRun = programRuns.get(component.type).find(pr => {
                                if (component.type === 'ECS') {
                                    // Key carries GPS project + vehicle + RPO, so matching on it alone
                                    // cannot load a different RPO's timing.
                                    return pr.getKey() === expectedKey;
                                }
                                return pr.programName === programName;
                            });
                            if (matchingRun) {
                                p(component, matchingRun.programName || programName, matchingRun);
                            }
                        }
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Program Run data from eSCI. Please try again later.');
                this.setComponentStateDeferred(component, 'loading', false);
                component.programRunsLookupFailed = true;
                (component as any).afterProgramRunsLoaded?.(programRuns);
            },
            complete: () => {
                this.setComponentStateDeferred(component, 'loading', false);
                (component as any).afterProgramRunsLoaded?.(programRuns);
            }
        });
        return programRuns;
    }

    public getProgramRunDetails(
        component: KtsComponent,
        programRunIds: number[],
        options?: { includeArchived?: boolean; onComplete?: () => void }
    ): void {
        const onComplete = options?.onComplete;
        if (!programRunIds || !programRunIds.length) {
            onComplete?.();
            return;
        }
        let params = new HttpParams();
        params = params.set('programRunIds', programRunIds.join(','));
        params = params.set('getIcdsNotOnSchedule', true);
        if (options?.includeArchived) {
            params = params.set('includeArchived', options.includeArchived);
        }
        this.makeServiceCallWithParams(this.getKtsProgramRunsUrl, params)
            .pipe(finalize(() => onComplete?.()))
            .subscribe({
                next: (event: any) => {
                    if (event instanceof HttpResponse && event.body?.length) {
                        (component as any).mergeProgramRunDetails?.(event.body);
                    }
                },
                error: (err: any) => {
                    console.log(err);
                    component.messageService.processError('Failed to get Program Run detail from eSCI. Please try again later.');
                }
            });
    }

    public getGpsRpos(component: GpsTimingComponent | GpsRpoManagementComponent, p?: () => void) {
        this.makeServiceCall(component.getGpsRposUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    // Use shape/url-based discrimination to avoid runtime circular imports.
                    const isGpsRpoManagement = !('gpsRpos' in component)
                        || component.getGpsRposUrl?.includes('/fromRpoManagement');
                    component.missingGpsRpos.length = 0;
                    let rows: GpsRpo[] = isGpsRpoManagement ? (component as any).rowData : component.gpsRpos;
                    rows.length = 0;
                    if (event.body && event.body.length > 0) {
                        // clear any filters while we refresh the data
                        if (this.isGridApiAlive(component.gridApi)) {
                            component.gridApi.setFilterModel(null);
                        }
                        if (isGpsRpoManagement) {
                            (component as any).rowData = [];
                            rows = (component as any).rowData;
                        } else {
                            component.gpsRpos = [];
                            rows = component.gpsRpos;
                        }
                        event.body.map(result => {
                            const gpsRpo = new GpsRpo();
                            gpsRpo.setData(result);
                            if (gpsRpo.id === -1) {
                                gpsRpo.state = 'new';
                                component.missingGpsRpos.push(gpsRpo);
                            } else {
                                gpsRpo.originalRowData = structuredClone(gpsRpo);
                                rows.push(gpsRpo);
                            }
                        });
                        this.syncRowDataSignal(component);
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get GPS RPO data from eSCI. Please try again later.');
            },
            complete: () => {
                if (p) {
                    p();
                }
            }
        });
    }

    public getMomentumProgramsForIcdTiming(component: IcdProgramTimingComponent, p?: () => void) {
        this.makeServiceCall(this.getMomentumProgramsForIcdTimingUrl).subscribe({
            next: (event: any) => {
                component.rowData = [];
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        component.rowData = [];
                        event.body.map(result => {
                            const momentumProgram = new MomentumProgram();
                            momentumProgram.setDetails(result);
                            momentumProgram.state = 'saved';
                            momentumProgram.originalRowData = structuredClone(momentumProgram);
                            component.rowData.push(momentumProgram);
                        });
                        // sort by program.programName and then model year
                        component.rowData.sort((a, b) => {
                            if (a.program.programName === b.program.programName) {
                                return a.modelYear.localeCompare(b.modelYear);
                            }
                            return a.program.programName.localeCompare(b.program.programName);
                        });
                    }
                    this.syncRowDataSignal(component);
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get ICD Program Timing data from eSCI. Please try again later.');
            },
            complete: () => {
                if (p) {
                    p();
                }
            }
        });
    }

    public getObjectDescription(component: string): Observable<ObjectDescription | undefined> {
        const objDesc = this.objDescriptionMapByName.get(component);
        if (objDesc) {
            // Return the cached result as an observable
            return of(objDesc);
        }
        return this.http.get<ObjectDescription>(`${environment.apiUrl}${this.getDescribeObjectUrl}/${component}`)
            .pipe(tap((data) => {
                    // Cache the result
                    this.objDescriptionMapByName.set(component, data);
                }),
                shareReplay(1) // Ensures only one request and shares result with multiple subscribers
            );
    }

    public getObjectDescriptions(): Observable<ObjectDescription[]> {
        if (this.objectDescriptions) {
            // Return the cached result as an observable
            return of(this.objectDescriptions);
        }
        return this.http.get<ObjectDescription[]>(`${environment.apiUrl}${this.getDescribeObjectsUrl}`)
            .pipe(tap((data) => {
                    // Cache the result
                    this.objectDescriptions = data as ObjectDescription[];
                    for (const objDesc of this.objectDescriptions) {
                        this.objDescriptionMapByName.set(objDesc.className, objDesc);
                    }
                }),
                shareReplay(1) // Ensures only one request and shares result with multiple subscribers
            );
    }

    public getComponentImageTypes(componentName: string): Observable<string[]> {
        if (this.componentImageTypes) {
            return of(this.componentImageTypes);
        }
        if (!this.componentImageTypesRequest$) {
            this.componentImageTypesRequest$ = this.http.get<string[]>(
                `${environment.apiUrl}${this.getComponentImageTypesUrl}/${componentName}`
            ).pipe(
                tap(imageTypes => this.componentImageTypes = imageTypes),
                finalize(() => this.componentImageTypesRequest$ = null),
                shareReplay(1)
            );
        }
        return this.componentImageTypesRequest$;
    }

    public getObjects(component: string, params?: { [key: string]: string | string[] }) {
        let httpParams = new HttpParams();
        if (params && Object.keys(params).length > 0) {
            Object.keys(params).forEach(key => {
                const val = params[key];
                if (Array.isArray(val)) {
                    val.forEach(v => { httpParams = httpParams.append(key, v); });
                } else if (val != null && val !== '') {
                    httpParams = httpParams.set(key, val);
                }
            });
        }
        const options = (params && Object.keys(params).length > 0) ? { params: httpParams } : {};
        return this.http.get(`${environment.apiUrl}${this.getObjectsUrl}/${component}`, options);
    }
    /**
     * One page of a grid that pages on the server, ordered and filtered by the server.
     */
    public getObjectsPaged(component: string, page: GridPageRequest): Observable<PagingResult<any>> {
        return this.getObjects(component, buildGridPageParams(page)) as Observable<PagingResult<any>>;
    }

    /**
     * The values a column can be filtered by, gathered from the whole table. A server-paged grid holds only one page,
     * so it cannot build its own filter option list from its rows.
     */
    public getObjectColumnValues(component: string, memberName: string): Observable<string[]> {
        return this.http.get<string[]>(
            `${environment.apiUrl}${this.getObjectColumnValuesUrl}/${component}/${memberName}`);
    }

    /**
     * The components of this type matching what the user typed, as the ids and labels a picker offers. A server-paged
     * grid holds one page, so the ids a row may be pointed at cannot come from its rows - but nor can the picker be
     * handed the catalog, which is the cost server paging exists to avoid. The server searches and answers with a
     * bounded number of matches.
     *
     * @param labelColumns the member names to show beside the id, taken from the component's own declaration
     * @param searchText   what the user typed; blank asks for the first bounded page of the catalog
     */
    public getObjectIdOptions(component: string, labelColumns: string[], searchText = '',
                              limit = 50): Observable<ComponentIdOption[]> {
        const params = new HttpParams().set('labels', (labelColumns || []).join(','))
                                       .set('search', searchText ?? '')
                                       .set('limit', String(limit));
        return this.http.get<ComponentIdOption[]>(
            `${environment.apiUrl}${this.getObjectIdOptionsUrl}/${component}`, {params})
                   .pipe(map(options => (options || [])
                       .map(option => ({id: Number(option?.id), labels: option?.labels ?? {}}))
                       .filter(option => Number.isFinite(option.id))));
    }

    /**
     * The rows named by a set of ids, in one request. Used to show something other than a number beside an id a row
     * points at when that row is not on the page the grid holds.
     */
    public getObjectsByIds(component: string, ids: number[]): Observable<PagingResult<any>> {
        const wanted = Array.from(new Set((ids || []).filter(id => Number.isFinite(id) && id > 0)));
        if (wanted.length === 0) {
            return of({results: [], total: 0} as PagingResult<any>);
        }
        return this.getObjects(component, {ids: wanted.map(id => String(id))}) as Observable<PagingResult<any>>;
    }

    /**
     * The report over every row matching the grid's filters and sort, built by the server rather than from the one
     * page the browser holds.
     */
    public exportObjects(component: string, page: GridPageRequest) {
        return this.http.get(`${environment.apiUrl}${this.exportObjectsUrl}/${component}`,
                             {params: buildGridPageParams(page, false), responseType: 'arraybuffer'});
    }

    public getObjectsForExport(component: string, page: GridPageRequest): Observable<PagingResult<any>> {
        return this.http.get<PagingResult<any>>(`${environment.apiUrl}${this.getObjectsForExportUrl}/${component}`,
                                                {params: buildGridPageParams(page)});
    }

    public getComponentIdPickerRows(source: string): Observable<any[]> {
        let rowsSubject = this.componentIdPickerRowsBySource.get(source);
        if (!rowsSubject) {
            rowsSubject = new ReplaySubject<any[]>(1);
            this.componentIdPickerRowsBySource.set(source, rowsSubject);
            this.loadComponentIdPickerRows(source, rowsSubject);
        }
        return rowsSubject.asObservable();
    }

    public refreshComponentIdPickerRows(source: string): void {
        const rowsSubject = this.componentIdPickerRowsBySource.get(source);
        if (!rowsSubject || this.componentIdPickerRefreshesInFlight.has(source)) {
            return;
        }
        this.componentIdPickerRefreshesInFlight.add(source);
        this.http.get<any[]>(`${environment.apiUrl}${this.getComponentIdPickerRowsUrl}/${source}`).pipe(
            finalize(() => this.componentIdPickerRefreshesInFlight.delete(source))
        ).subscribe({
            next: rows => rowsSubject.next(rows),
            error: () => {
                // Keep the last successful picker rows available; the next save can retry the refresh.
            }
        });
    }

    private loadComponentIdPickerRows(source: string, rowsSubject: ReplaySubject<any[]>): void {
        this.http.get<any[]>(`${environment.apiUrl}${this.getComponentIdPickerRowsUrl}/${source}`).subscribe({
            next: rows => rowsSubject.next(rows),
            error: error => {
                this.componentIdPickerRowsBySource.delete(source);
                rowsSubject.error(error);
            }
        });
    }

    private refreshComponentIdPickerRowsForComponent(componentName: string): void {
        const source = EsciService.componentIdPickerSourceByComponentName[componentName];
        if (source) {
            this.refreshComponentIdPickerRows(source);
        }
    }

    public getObjectsByPage(component: string, limit: number, offset: number) {
        return this.http.get(`${environment.apiUrl}${this.getObjectsUrl}/${component}?limit=${limit}&offset=${offset}`);
    }

    public getHistory(component: string) {
        return this.http.get(`${environment.apiUrl}${this.getHistoryUrl}/${component}`,
                             {params: {}, responseType: 'arraybuffer'});
    }

    public getHistoryByComponentId(component: string, componentId: number) {
        return this.http.get<any[]>(`${environment.apiUrl}${this.getHistoryUrl}/${component}/${componentId}`);
    }

    public saveObjects(component: EditableGridComponent, componentName: string, dataRows: any[]) {
        if (!dataRows || dataRows.length === 0) {
            this.setComponentStateDeferred(component, 'saving', false);
            return;
        }
        this.setComponentStateDeferred(component, 'saving', true);

        for (const row of dataRows) {
            // Append the row as a JSON string, but ignore the elements we've added locally:
            const {imageFile, imageFiles, attachmentFile, validationReportFile, onSuccess, onError, ...safeRow} = row;
            EsciService.removeUiOnlySaveMetadata(safeRow);
            const validationReportFiles = this.collectValidationReportFiles(row, safeRow);
            if (validationReportFile instanceof File) {
                validationReportFiles.unshift(validationReportFile);
            }

            // PPCO is now handled by the backend during component save
            // Just send the component with PPCO data, backend will create/update as needed
            this.saveComponent(component, componentName, row, safeRow, imageFile, imageFiles, attachmentFile,
                               validationReportFiles, onSuccess, onError);
        }
    }

    /**
     * Saves only PPCO data for a component. Used by PURCH_ADMIN users who don't have
     * component edit permissions but can still update PPCO data.
     */
    public savePPCOOnly(component: EditableGridComponent, componentName: string, dataRows: any[]) {
        this.setComponentStateDeferred(component, 'saving', true);

        for (const row of dataRows) {
            const {imageFile, imageFiles, attachmentFile, onSuccess, ...safeRow} = row;
            EsciService.removeUiOnlySaveMetadata(safeRow);

            const formData = new FormData();
            EsciService.normalizeMultiImageListFieldsForSave(safeRow, this.componentImageTypes ?? []);
            formData.append('component', JSON.stringify(safeRow));

            this.http.post(`${environment.apiUrl}/esciUI/savePPCOOnly/${componentName}`, formData,
                {observe: 'response'}
            ).subscribe({
                next: (event: any) => {
                    EsciService.handleSaveResponse(event, component, 'PPCO');
                    this.setComponentStateDeferred(component, 'saving', false);
                    if (event instanceof HttpResponse && event.status === 200) {
                        // Defer row-level success handlers so UI mutations (for example id -1 -> new id)
                        // happen after the current Angular check cycle.
                        setTimeout(() => row.onSuccess?.(event.body), 0);
                    }
                },
                error: (err: any) => {
                    EsciService.handleError(err, component);
                    this.setComponentStateDeferred(component, 'saving', false);
                }
            });
        }
    }

    /**
     * Saves only the Tier-N Resilient FK on a component. Used by COMPONENT_TIER_N_ADMIN users
     * who don't have full component edit permissions but can still update Tier-N. Mirrors
     * {@link savePPCOOnly} but talks to the dedicated /saveTierNResilientOnly/{className} endpoint.
     */
    public saveTierNResilientOnly(component: EditableGridComponent, componentName: string, dataRows: any[]) {
        this.setComponentStateDeferred(component, 'saving', true);

        for (const row of dataRows) {
            const {imageFile, imageFiles, attachmentFile, onSuccess, ...safeRow} = row;
            EsciService.removeUiOnlySaveMetadata(safeRow);

            const formData = new FormData();
            EsciService.normalizeMultiImageListFieldsForSave(safeRow, this.componentImageTypes ?? []);
            formData.append('component', JSON.stringify(safeRow));

            this.http.post(`${environment.apiUrl}/esciUI/saveTierNResilientOnly/${componentName}`, formData,
                {observe: 'response'}
            ).subscribe({
                next: (event: any) => {
                    EsciService.handleSaveResponse(event, component, 'Tier-N Resilient');
                    this.setComponentStateDeferred(component, 'saving', false);
                    if (event instanceof HttpResponse && event.status === 200) {
                        setTimeout(() => row.onSuccess?.(event.body), 0);
                    }
                },
                error: (err: any) => {
                    EsciService.handleError(err, component);
                    this.setComponentStateDeferred(component, 'saving', false);
                }
            });
        }
    }

    /** Normalize legacy image-list values to the name-keyed map expected by the backend. */
    private static normalizeMultiImageListFieldsForSave(safeRow: Record<string, unknown>,
                                                        imageTypes: readonly string[]): void {
        if (!safeRow || typeof safeRow !== 'object') {
            return;
        }
        for (const key of Object.keys(safeRow)) {
            const v = safeRow[key];
            if (key === 'partImageFileIds' && Array.isArray(v)) {
                safeRow[key] = {General: [...v]};
                continue;
            }
            if (v != null && typeof v === 'object' && !Array.isArray(v) && Array.isArray((v as { fileIds?: unknown }).fileIds)) {
                const o = v as { fileIds: number[]; imageFiles?: unknown[] };
                const newCount = Array.isArray(o.imageFiles)
                    ? o.imageFiles.filter((f): f is File => f instanceof File).length
                    : 0;
                safeRow[key] = {General: [...o.fileIds, ...Array(newCount).fill(-2)]};
            } else if (key === 'partImageFileIds' && v != null && typeof v === 'object') {
                const normalizedImageIds: Record<string, number[]> = {};
                for (const [name, fileIds] of Object.entries(v)) {
                    if (Array.isArray(fileIds)) {
                        const imageType = normalizeComponentImageType(name, imageTypes);
                        (normalizedImageIds[imageType] ??= []).push(...fileIds);
                    }
                }
                safeRow[key] = normalizedImageIds;
            }
        }
    }

    /**
     * Multipart order must match the map's -2 placeholder order so the backend can assign each file's name.
     */
    private static orderNamedImageFiles(imageFiles: unknown[], imageIdsByName: unknown,
                                        imageTypes: readonly string[]): File[] {
        const unnamedFiles: File[] = [];
        const filesByName = new Map<string, File[]>();
        for (const imageFile of imageFiles) {
            if (imageFile instanceof File) {
                unnamedFiles.push(imageFile);
                continue;
            }
            const namedFile = imageFile as {name?: unknown; file?: unknown};
            if (namedFile?.file instanceof File) {
                const name = normalizeComponentImageType(namedFile.name, imageTypes);
                const files = filesByName.get(name) ?? [];
                files.push(namedFile.file);
                filesByName.set(name, files);
            }
        }
        if (imageIdsByName == null || typeof imageIdsByName !== 'object' || Array.isArray(imageIdsByName)) {
            return [...unnamedFiles, ...Array.from(filesByName.values()).flat()];
        }
        const results: File[] = [];
        for (const name of imageTypes) {
            const fileIds = getComponentImageIdsForType(imageIdsByName, name, imageTypes);
            const files = filesByName.get(name) ?? [];
            fileIds.filter(fileId => fileId === -2)
                   .forEach(() => {
                       const file = files.shift();
                       if (file) {
                           results.push(file);
                       }
                   });
        }
        return [...results, ...unnamedFiles, ...Array.from(filesByName.values()).flat()];
    }

    // Note: intentionally does NOT strip `updateMessage` - it's a front-end-only reason the grid sets on
    // a Development Status Approved -> Draft/Pending downgrade, and must survive to reach the backend
    // (see EsciComponentGridComponent.promptForDevelopmentStatusReasonIfNeeded and
    // Component.getUpdateFieldMessage on the backend).
    private static removeUiOnlySaveMetadata(safeRow: Record<string, unknown>): void {
        if (!safeRow || typeof safeRow !== 'object') {
            return;
        }
        delete safeRow.componentName;
        delete safeRow.rowNumber;
        delete safeRow.fieldRules;
        delete safeRow.originalRowData;
        delete safeRow.changes;
        delete safeRow.state;
        delete safeRow.dataChanged;
        delete safeRow._cellValidationErrors;
        delete safeRow._conglomerationOutOfSyncFields;
        delete safeRow._conglomerationOutOfSyncMessage;
    }

    private saveComponent(component: EditableGridComponent, componentName: string, row: any, safeRow: any,
                          imageFile: any, imageFiles: any[], attachmentFile: any, validationReportFiles: File[],
                          onSuccess: any, onError: any) {
        // Create a FormData object to hold the row and files
        const formData = new FormData();

        // Append files if they are provided
        if (imageFile) {
            formData.append('imageFiles', imageFile);
        }
        if (imageFiles && Array.isArray(imageFiles)) {
            EsciService.orderNamedImageFiles(imageFiles, safeRow.partImageFileIds, this.componentImageTypes ?? [])
                .forEach(file => formData.append('imageFiles', file));
        }
        if (attachmentFile) {
            formData.append('attachmentFile', attachmentFile);
        }
        if (Array.isArray(validationReportFiles)) {
            validationReportFiles
                .filter((file): file is File => file instanceof File)
                .forEach(file => formData.append('validationReportFiles', file));
        }
        // When an object contains a class that derives from AbstractConnector,
        // we need to qualify so JSON will know how to deserialize it:
        if ('connectorService' in safeRow && safeRow.connectorService) {
            safeRow.connectorService['@type'] = 'ConnectorService';
        }
        if ('connectorPurchase' in safeRow && safeRow.connectorPurchase) {
            safeRow.connectorPurchase['@type'] = 'ConnectorPurchase';
        }
        // PPCO handling is now done in the backend - just pass the ppco object as-is
        // Backend will create new PPCO records and link them properly

        EsciService.normalizeMultiImageListFieldsForSave(safeRow, this.componentImageTypes ?? []);

        formData.append('component', JSON.stringify(safeRow));

        this.http.post(`${environment.apiUrl}${component.saveUrl}`, formData,
            {observe: 'response'}  // Returns full HttpResponse<YourResponseType>
        ).subscribe({
            next: (event: any) => {
                EsciService.handleSaveResponse(event, component, componentName);
                this.setComponentStateDeferred(component, 'saving', false);

                if (event instanceof HttpResponse && event.status === 200) {
                    this.refreshComponentIdPickerRowsForComponent(componentName);
                    // This was an add, update the object to have to new id.
                    // Defer row-level success handlers so UI mutations (for example id -1 -> new id)
                    // happen after the current Angular check cycle.
                    setTimeout(() => row.onSuccess?.(event.body), 0);
                }
            },
            error: (err: any) => {
                EsciService.handleError(err, component);
                this.setComponentStateDeferred(component, 'saving', false);
                // The failure goes with it: only the grid knows which row this was and what its values mean, so it is
                // the only place a refusal like a duplicate can be shown on the row itself.
                onError?.(err);
            }
        });
    }

    private collectValidationReportFiles(row: any, safeRow: any): File[] {
        const collectedFiles: File[] = [];
        const rowDocs = Array.isArray(row?.componentDocuments) ? row.componentDocuments : [];
        if (Array.isArray(safeRow?.componentDocuments)) {
            safeRow.componentDocuments = safeRow.componentDocuments.map((doc: any) =>
                doc && typeof doc === 'object' ? {...doc} : doc
            );
        }
        const safeDocs = Array.isArray(safeRow?.componentDocuments) ? safeRow.componentDocuments : [];
        for (let i = 0; i < rowDocs.length; i++) {
            const doc = rowDocs[i];
            if (doc?.validationReportFile instanceof File) {
                collectedFiles.push(doc.validationReportFile);
            }
            if (safeDocs[i] && Object.prototype.hasOwnProperty.call(safeDocs[i], 'validationReportFile')) {
                delete safeDocs[i].validationReportFile;
            }
        }
        return collectedFiles;
    }

    public deleteObjectsByIds(component: EditableGridComponent, componentName: string, ids: number[], onSuccess: () => void) {
        if (!ids || ids.length === 0) {
            this.setComponentStateDeferred(component, 'saving', false);
            if (onSuccess) {
                onSuccess();
            }
            return;
        }
        let params = new HttpParams();
        ids.forEach(id => {
            params = params.append('ids', String(id));
        });
        this.setComponentStateDeferred(component, 'saving', true);
        return this.http.delete(`${environment.apiUrl}${this.getObjectsUrl}/${componentName}`,
            {params, observe: 'response'})
            .pipe(
                timeout(15000),
                finalize(() => {
                    this.setComponentStateDeferred(component, 'saving', false);
                })
            )
            .subscribe({
                next: (event: any) => {
                    EsciService.handleSaveResponse(event, component, componentName);
                    if (onSuccess) {
                        onSuccess();
                    }
                },
                error: (err: any) => {
                    EsciService.handleError(err, component);
                }
            });
    }


    getGpsMomentumData(component: GpsTimingComponent, p?: () => void) {
        this.setComponentStateDeferred(component, 'loading', true);
        this.makeServiceCall(this.getGpsMomentumDataUrl).subscribe({
            next: (event: any) => {
                component.momentumPrograms = [];
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        event.body.map(program => {
                            const mp = new MomentumProgram();
                            mp.momentumId = program.PROGRAM_ID;
                            mp.programName = program.PROGRAM_NAME;
                            mp.setDetails(program);
                            component.momentumPrograms.push(mp);
                        });
                    }
                }
            }, complete: () => {
                this.setComponentStateDeferred(component, 'loading', false);
                if (p) {
                    p();
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Momentum data from eSCI. Please try again later.');
                this.setComponentStateDeferred(component, 'loading', false);
            }
        });
    }

    public getGpsTiming(component: EditableGridComponent, p?: () => void) {
        this.makeServiceCall(this.getGpsTimingUrl).subscribe({
            next: (event: any) => {
                component['gpsTimingFromDB'] = [];
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        event.body.map(result => {
                            const gpsTiming = new GpsTiming();
                            gpsTiming.setData(result);
                            component['gpsTimingFromDB'].push(gpsTiming);
                        });
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get GPS Timing data from eSCI. Please try again later.');
                this.setComponentStateDeferred(component, 'loading', false);
            },
            complete: () => {
                if (p) {
                    p();
                }
            }
        });
    }

    saveRows(component: EditableGridComponent, data: ValidationModel[], dataTypeForMessage: string,
             successCallBack?: () => void) {
        this.setComponentStateDeferred(component, 'saving', true);
        data.forEach(model => {
            const row = (model as any);
            if ('connectorService' in row && row.connectorService) {
                row.connectorService['@type'] = 'ConnectorService';
            }
            if ('connectorPurchase' in row && row.connectorPurchase) {
                row.connectorPurchase['@type'] = 'ConnectorPurchase';
            }
        });

        this.http.post(`${environment.apiUrl}${component.saveUrl}`, data, {headers: new HttpHeaders({'Content-Type': 'application/json'})}).subscribe({
            next: (event: any) => {
                component.messageService.processSuccess(`${dataTypeForMessage} data saved successfully.`);
                this.setComponentStateDeferred(component, 'saving', false);
                if (successCallBack) {
                    successCallBack();
                }
            },
            error: (err: any) => {
                EsciService.handleError(err, component);
                this.setComponentStateDeferred(component, 'saving', false);
            }
        });
    }

    public getRestrictedMomentumProgramList(component: RestrictedProgramsComponent, p?: () => void) {
        this.makeServiceCall(this.getRestrictedMomentumProgramsUrl).subscribe({
            next: (event: any) => {
                component.restrictedPrograms = [];
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        event.body.map(result => {
                            const program = new MomentumProgram();
                            program.setDetails(result);
                            // high level call does not have program name split out. use regex to remove starting model year
                            program.projectName = program.programName;
                            program.programName = program.programName.replace(/^\d{4} /, '');
                            component.restrictedPrograms.push(new RestrictedMomentumProgram(program));
                        });
                    }
                    // sort restricted programs by program name
                    component.restrictedPrograms.sort((a, b) => a.momentumProgram.programName.localeCompare(b.momentumProgram.programName));
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Restricted Momentum Programs from eSCI. Please try again later.');
            },
            complete: () => {
                // set the allowed users in each restricted program
                component.esciService.getMomentumProgramAccess(component, p);
            }
        });
    }

    public getMomentumProgramAccess(component: RestrictedProgramsComponent, p?: () => void) {
        this.makeServiceCall(this.getMomentumProgramAccessUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    const accessList = [];
                    if (event.body && event.body.length > 0) {
                        event.body.map(result => {
                            const momentumProgramAccess = new MomentumProgramAccess();
                            momentumProgramAccess.setData(result);
                            accessList.push(momentumProgramAccess);
                        });
                    }
                    component.restrictedPrograms.forEach(restrictedProgram => {
                        const programAccessList = accessList.filter(o => o.momentumId === restrictedProgram.momentumProgram.momentumId);
                        restrictedProgram.setProgramAccess(programAccessList);
                    });
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Restricted Momentum Programs from eSCI. Please try again later.');
            },
            complete: () => {
                if (p) {
                    p();
                }
            }
        });
    }

    deleteKtsTemplate(version: string, component: KtsComponent, p?: () => void) {
        if (!version) {
            return;
        }
        const params = new HttpParams().set('version', version);
        this.makeServiceCallWithParams(this.deleteKtsTemplateUrl, params).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    if (event.status === 200) {
                        component.messageService.processSuccess(`KTS Template ${version} deleted successfully.`);
                    } else {
                        component.messageService.processError(`KTS Template ${version} failed to delete.`);
                    }
                }
            },
            error: (err: any) => {
                EsciService.handleError(err, component);
                this.setComponentStateDeferred(component, 'loading', false);
                if (p) {
                    p();
                }
            },
            complete: () => {
                if (p) {
                    p();
                }
            }
        });
    }

    getIcdProgramsForIcdTiming(component: IcdProgramTimingComponent, completionCallback?: () => void) {
        this.makeServiceCall(this.getIcdProgramsForIcdTimingUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        event.body.forEach(program => {
                            component.programs.set(program.programName, program);
                        });
                        if (completionCallback) {
                            completionCallback();
                        }
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Programs from eSCI. Please try again later.');
            }
        });
    }

    /**
     * Returns the list of program names the calling user is allowed to operate on for the
     * IO Matrix Workflow. Mirrors the classic eSCI ICD create page contract: restricted
     * programs the user is not assigned to are excluded server-side, so they never appear
     * in the IO Matrix Workflow program dropdown.
     */
    public getAllowedIoMatrixProgramOptions(): Observable<string[]> {
        return this.http.get<any[]>(`${environment.apiUrl}${this.getAllowedIoMatrixProgramsUrl}`).pipe(
            map(programs => EsciService.distinctSortedProgramNames(programs))
        );
    }

    /**
     * Distinct program names for a dropdown, alphabetically ordered, with blanks dropped.
     *
     * <p>Explicit comparator: a bare sort() orders by UTF-16 code unit, which would put every upper-case
     * name ahead of every lower-case one. The type guard in filter is what lets the comparator see string
     * rather than string | undefined. Shared by the powertrain and aggregator allowed-program endpoints,
     * which had byte-identical copies of this.
     */
    private static distinctSortedProgramNames(programs: any[]): string[] {
        return [...new Set((programs || [])
            .map(program => program?.programName)
            .filter((name: string | undefined): name is string => !!name)
        )].sort((left, right) => left.localeCompare(right));
    }

    /**
     * Distinct allowed programs for Create Aggregator ICDs, alphabetically by name, blanks dropped.
     * Keeps Type fields so the page can warn when the program axis differs from the matrix power type.
     */
    private static distinctSortedAllowedPrograms(
        programs: AggregatorAllowedProgram[] | null | undefined): AggregatorAllowedProgram[] {
        const byName = new Map<string, AggregatorAllowedProgram>();
        for (const program of programs || []) {
            const programName = program?.programName;
            if (programName && !byName.has(programName)) {
                byName.set(programName, {
                    programName,
                    programTypeList: Array.isArray(program.programTypeList) ? [...program.programTypeList] : [],
                    programTypeDisplay: program.programTypeDisplay || '',
                    resolvedPowerType: program.resolvedPowerType || null
                });
            }
        }
        return [...byName.values()].sort((left, right) => left.programName.localeCompare(right.programName));
    }

    public getIcdModelYearOptionsFromReference(): Observable<string[]> {
        return this.http.get<any[]>(`${environment.apiUrl}${this.getMomentumProgramsForIcdTimingUrl}`).pipe(
            map((programs: any[]) => [...new Set((programs || [])
                .map(program => program?.modelYear)
                .filter((year: string | undefined) => !!year)
            )].sort((a, b) => a.localeCompare(b)))
        );
    }

    getProgramRunsForIcdTiming(component: IcdProgramTimingComponent, completionCallback?: () => void) {
        this.makeServiceCall(this.getKtsProdProgramRunsForIcdTimingUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        event.body.map((result: any) => {
                            const pr = new ProgramRun();
                            pr.setProgramRunFromEsciData(result);
                            // This caused a bug in the past if AVD and PROD type timings
                            // exist for a single program. We now only return PROD type
                            // if that ever changes, this map will need a more unique key
                            component.programRuns.set(pr.programName, pr);
                        });
                        component.programRuns.set('', new ProgramRun());
                        if (completionCallback) {
                            completionCallback();
                        }
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Program Runs from eSCI. Please try again later.');
            }
        });
    }

    updateIcdDates(component: IcdProgramTimingComponent, rowsToUse: MomentumProgram[], completionCallback?: () => void) {
        this.setComponentStateDeferred(component, 'saving', true);
        this.http.post(`${environment.apiUrl}${component.updateIcdsUrl}`, rowsToUse, {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
        }).subscribe({
            next: (responseBody: any) => {
                component.messageService.processSuccess(`Stage Due Dates updated successfully on ${responseBody} ICDs.`);
                this.setComponentStateDeferred(component, 'saving', false);
                if (this.isGridApiAlive(component.gridApi)) {
                    component.gridApi.deselectAll();
                }
                if (completionCallback) {
                    completionCallback();
                }
            },
            error: (err: any) => {
                EsciService.handleError(err, component);
                this.setComponentStateDeferred(component, 'saving', false);
            }
        });
    }

    savePrimaryTimingPrograms(component: KtsComponent, programRuns: ProgramRun[], completionCallback?: () => void) {
        this.setComponentStateDeferred(component, 'saving', true);
        this.http.post(`${environment.apiUrl}${this.savePrimaryTimingProgramsUrl}`, programRuns, {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
        }).subscribe({
            next: (responseBody: any) => {
                component.messageService.processSuccess(`Primary Timing Programs updated successfully.`);
                this.setComponentStateDeferred(component, 'saving', false);
                if (completionCallback) {
                    completionCallback();
                }
            },
            error: (err: any) => {
                EsciService.handleError(err, component);
                this.setComponentStateDeferred(component, 'saving', false);
            }
        });

    }

    getWorkloadTimingSize(component: WorkloadTimingComponent, completionCallback?: () => void) {
        this.makeServiceCall(this.getWorkloadTimingSizeUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        event.body.map((result: any) => {
                            const wts = new WorkloadTimingSize();
                            wts.setData(result);
                            component.workloadTimingSizes.push(wts);
                        });
                        if (completionCallback) {
                            completionCallback();
                        }
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Workload Timing Size from eSCI. Please try again later.');
            }
        });
    }

    getProgramSize(component: WorkloadTimingComponent, completionCallback?: () => void) {
        this.makeServiceCall(this.getProgramSizeUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    component.rowData.length = 0;
                    if (event.body && event.body.length > 0) {
                        event.body.map((result: any) => {
                            const ps = new ProgramSize();
                            ps.setData(result);
                            ps.originalRowData = structuredClone(ps);
                            component.rowData.push(ps);
                        });
                        if (completionCallback) {
                            completionCallback();
                        }
                    }
                    this.syncRowDataSignal(component);
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Program Size from eSCI. Please try again later.');
            }
        });
    }

    getKtsTimingTemplatePrefixes(component: KtsEditorComponent) {
        this.makeServiceCall(this.getKtsTimingTemplatePrefixesUrl).subscribe({
            next: (event: any) => {
                if (event instanceof HttpResponse) {
                    if (event.body && event.body.length > 0) {
                        component.versionPrefixes = event.body;
                        // add empty option for the dropdown
                        component.versionPrefixes.push('');
                    }
                }
            },
            error: (err: any) => {
                console.log(err);
                component.messageService.processError('Failed to get Template Version Prefixes from eSCI. Please try again later.');
            }
        });
    }

    deleteProgramRuns(component: KtsComponent, programRuns: ProgramRun[], completionCallback?: () => void) {
        component.messageService.clear();
        const programRunIds = programRuns.map((programRun) => programRun.id);
        this.http.post(`${environment.apiUrl}${this.deleteProgramRunsUrl}`, programRunIds, {
            headers: new HttpHeaders({'Content-Type': 'application/json'}),
        }).subscribe({
            next: (programRunIdsNotDeleted: any) => {
                // convert list of program runs into program run names where the id is not in programRunIdsNotDeleted
                const programRunNamesDeleted = programRuns.filter((programRun) => !programRunIdsNotDeleted.includes(programRun.id))
                                                          .map((programRun) => programRun.programName);
                if (programRunIdsNotDeleted && programRunIdsNotDeleted.length) {
                    // convert list of program run ids into program run names
                    const programRunNamesNotDeleted = programRunIdsNotDeleted.map((programRunId: any) => {
                        const programRun = programRuns.find((pr) => pr.id === programRunId);
                        return programRun ? programRun.programName : '';
                    }).filter((name: string) => name !== '');
                    component.messageService.processError(`Program Timings could not be deleted because they are
                                                            in use in ICD Program Timing: ${programRunNamesNotDeleted}`);
                }
                if (programRunNamesDeleted && programRunNamesDeleted.length) {
                    component.messageService.processSuccessWithoutClear(`Program Timings deleted successfully: ${programRunNamesDeleted}`);
                }
                if (completionCallback) {
                    completionCallback();
                }
            },
            error: (err: any) => {
                EsciService.handleError(err, component);
            }
        });
    }

    // DiagTestProbe Kit Relationship methods
    public getDiagTestProbeLinks(diagTestProbeId: number): Observable<any> {
        return this.http.get(`${environment.apiUrl}/esciUI/diagTestProbeRelationships`, {
            params: {id: diagTestProbeId.toString()}
        });
    }

    public createDiagTestProbeLink(link: any): Observable<any> {
        // The backend expects an array of DiagTestProbeLink objects
        return this.http.post(`${environment.apiUrl}/esciUI/saveDiagTestProbeRelationship`, [link], {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    public deleteDiagTestProbeLink(link: any): Observable<any> {
        return this.http.post(`${environment.apiUrl}/esciUI/delete/diagTestProbeRelationship`, link, {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    // TerminalReleaseTool Kit Relationship methods
    public getTerminalReleaseToolLinks(terminalReleaseToolId: number): Observable<any> {
        return this.http.get(`${environment.apiUrl}/esciUI/terminalReleaseToolRelationships`, {
            params: {id: terminalReleaseToolId.toString()}
        });
    }

    public createTerminalReleaseToolLink(link: any): Observable<any> {
        // The backend expects an array of TerminalReleaseLink objects
        return this.http.post(`${environment.apiUrl}/esciUI/saveTerminalReleaseToolRelationship`, [link], {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    public deleteTerminalReleaseToolLink(link: any): Observable<any> {
        return this.http.post(`${environment.apiUrl}/esciUI/delete/terminalReleaseToolRelationship`, link, {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
        });
    }

    // PPCO (Purchase Price Cost Object) methods
    public getPPCOById(ppcoId: number): Observable<any> {
        return this.http.get(`${environment.apiUrl}/ui/ppco/${ppcoId}`);
    }

    public savePPCO(ppco: any): Observable<any> {
        if (ppco.id && ppco.id > 0) {
            // Update existing PPCO
            return this.http.put(`${environment.apiUrl}/ui/ppco/update/${ppco.id}`, ppco, {
                headers: new HttpHeaders({'Content-Type': 'application/json'})
            });
        } else {
            // Create new PPCO
            return this.http.post(`${environment.apiUrl}/ui/ppco/create`, ppco, {
                headers: new HttpHeaders({'Content-Type': 'application/json'})
            });
        }
    }

    public getUrlRequestActivityReport(component: EsciComponent, timeGrouping: string,
                                       completionCallback?: () => void): Observable<UrlRequestActivityReport[]> {
        const params = new HttpParams().set('timeGrouping', timeGrouping);
        const req = new HttpRequest('GET', `${environment.apiUrl}${this.getUrlRequestActivityReportUrl}`,
                                    {params: params});
        return new Observable(observer => {
            this.http.request(req).subscribe({
                next: (event: any) => {
                    if (event instanceof HttpResponse) {
                        if (event.body && event.body.length > 0) {
                            observer.next(event.body);
                        } else {
                            observer.next([]);
                        }
                        if (completionCallback) {
                            completionCallback();
                        }
                        observer.complete();
                    }
                },
                error: (err: any) => {
                    console.log(err);
                    component.messageService.processError('Failed to get URL Request Activity Report from eSCI. Please try again later.');
                    observer.error(err);
                }
            });
        });
    }

    public getUrlRequestLogs(component: EsciComponent, offset: number = 0, limit: number = 1000,
                             completionCallback?: () => void): Observable<PagingResult<UrlRequestLog>> {
        const params = new HttpParams()
            .set('offset', offset.toString())
            .set('limit', limit.toString());
        const req = new HttpRequest('GET', `${environment.apiUrl}${this.getUrlRequestLogsUrl}`, {params: params});
        return new Observable(observer => {
            this.http.request(req).subscribe({
                next: (event: any) => {
                    if (event instanceof HttpResponse) {
                        if (event.body) {
                            observer.next(event.body);
                        } else {
                            observer.next({results: [], total: 0});
                        }
                        if (completionCallback) {
                            completionCallback();
                        }
                        observer.complete();
                    }
                },
                error: (err: any) => {
                    console.log(err);
                    component.messageService.processError('Failed to get URL Request Logs from eSCI. Please try again later.');
                    observer.error(err);
                }
            });
        });
    }

    // -------------------------------------------------------------------------
    // Capital Component Usage async job API
    // -------------------------------------------------------------------------

    /**
     * Submits a Capital component-usage job. Returns immediately (202 Accepted)
     * with the assigned jobId. Use {@link pollCapitalJobStatus} to track progress.
     */
    public submitCapitalJob(request: {
        partType: string;
        partNumber?: string;
        objectCode?: string;
        objectCodeInstance?: string;
        circuitNumber?: string;
    }): Observable<{ jobId: string }> {
        return this.http.post<{ jobId: string }>(
            `${environment.apiUrl}${this.capitalComponentUsageSubmitUrl}`, request
        );
    }

    /**
     * Polls the status of a previously submitted Capital job.
     * Returns {@code {jobId, status, message}} where status is one of
     * PENDING | IN_PROGRESS | COMPLETE | FAILED.
     */
    public pollCapitalJobStatus(jobId: string): Observable<{ jobId: string; status: string; message: string }> {
        return this.http.get<{ jobId: string; status: string; message: string }>(
            `${environment.apiUrl}${this.capitalComponentUsageStatusUrl}${jobId}`
        );
    }


}
